<template>

<com-item name="games.universe" :size="size" :loading="loading" :is-empty="!active" title="Game Universe" class="universe-item">

	<template v-slot:about>

		<p>All the games {{ you }}'ve ever touched, arranged by how close they are to {{ your }} current gaming life.</p>

	</template>

	<template v-slot:buttons>
		<div class="universe-legend" v-if="active && !isFullscreen && !$isMobile">
			<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#4881bb"></span>Own</div>
			<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#7a5fbf"></span>Prev. Owned</div>
			<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#2FC482"></span>Played</div>
			<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#f5a623"></span>Rated</div>
			<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#e85d75"></span>Wishlisted</div>
		</div>
		<com-toggle v-if="active && !isFullscreen && !$isMobile" :options="linkOptions" :value="linkType" v-on:change="onLinkTypeChange" />
		<core-button v-if="active && !isFullscreen && (zoom > 1.05 || panX !== 0 || panY !== 0)" v-on:click.native="resetView" tooltip="Re-center" icon="crosshairs" :iconOnly="true" />
		<core-button v-if="active && !isFullscreen && !$isMobile" v-on:click.native="toggleFullscreen" tooltip="Fullscreen" icon="expand" :iconOnly="true" />
	</template>

	<template v-slot:stats>

		<div class="universe">

			<portal to="top" :disabled="!isFullscreen">
			<div class="universe-canvas-wrapper" ref="wrapper" :class="{'is-fullscreen': isFullscreen}">
				<canvas ref="canvas" v-on:mousemove="onMouseMove" v-on:mouseleave="onMouseLeave" v-on:click="onClick" v-on:wheel.prevent="onWheel" v-on:mousedown="onMouseDown" v-on:touchstart.prevent="onTouchStart" v-on:touchmove.prevent="onTouchMove" v-on:touchend.prevent="onTouchEnd"></canvas>

				<!-- Fullscreen overlay controls -->
				<div class="universe-overlay" v-if="isFullscreen">
					<div class="universe-overlay-legend" v-if="!$isMobile">
						<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#4881bb"></span>Own</div>
						<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#2FC482"></span>Played</div>
						<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#f5a623"></span>Rated</div>
						<div class="universe-legend-item"><span class="universe-legend-dot" style="background:#e85d75"></span>Wishlisted</div>
					</div>
					<com-toggle v-if="!$isMobile" :options="linkOptions" :value="linkType" v-on:change="onLinkTypeChange" theme="white" />
					<div class="universe-overlay-buttons">
						<button class="universe-overlay-button" v-if="zoom > 1.05 || panX !== 0 || panY !== 0" v-on:click="resetView" title="Re-center"><i class="fa fa-crosshairs"></i></button>
						<button class="universe-overlay-button" v-on:click="toggleFullscreen" title="Exit fullscreen"><i class="fa fa-compress"></i></button>
					</div>
				</div>

				<!-- Mobile tap hint — tapping enters fullscreen -->
				<div class="universe-tap-hint" v-if="$isMobile && !isFullscreen && active" v-on:click="toggleFullscreen"></div>

				<!-- Hover tooltip (lightweight) -->
				<div class="universe-tooltip" v-if="tooltip && !tooltip.isSegment" :style="tooltipStyle" :data-corner="tooltipCorner">
					<div class="universe-tooltip-name">{{ tooltip.name }}</div>
					<div class="universe-tooltip-meta" v-if="tooltip.lastLabel">{{ tooltip.lastLabel }} {{ tooltip.lastDate }}</div>
					<div class="universe-tooltip-connected" v-if="tooltip.connectedLabel">{{ tooltip.connectedLabel }}</div>
					<div class="universe-tooltip-hint">Click for more</div>
				</div>

				<!-- Selected tooltip: desktop (positioned near dot) -->
				<div class="universe-tooltip is-selected" v-if="selectedTooltip && !$isMobile" :style="selectedTooltipStyle" :data-corner="selectedTooltipCorner">
					<button class="universe-tooltip-close" v-on:click.stop="deselectGame"><i class="fa fa-times"></i></button>
					<div class="universe-tooltip-head">
						<div class="universe-tooltip-name">{{ selectedTooltip.name }}</div>
						<div class="universe-tooltip-year" v-if="selectedTooltip.year">{{ selectedTooltip.year }}</div>
					</div>
					<div class="universe-tooltip-metrics">
						<com-overview :label="selectedTooltip.ownLabel" type="date" :value="selectedTooltip.ownedDateRaw" :empty="!selectedTooltip.ownedDateRaw" :inset="true" />
						<com-overview v-if="selectedTooltip.rating" label="Rating" type="rating" :value="selectedTooltip.rating" :inset="true" />
						<com-overview label="Plays" type="number" :value="selectedTooltip.plays" :empty="!selectedTooltip.plays" :inset="true" />
						<com-overview v-if="selectedTooltip.firstPlayedRaw" label="First Played" type="date" :value="selectedTooltip.firstPlayedRaw" :inset="true" />
						<com-overview v-if="selectedTooltip.lastPlayedRaw" label="Last Played" type="date" :value="selectedTooltip.lastPlayedRaw" :inset="true" />
						<com-overview label="Connections" type="number" :value="selectedTooltip.connections" :empty="!selectedTooltip.connections" :inset="true" :sublabel="selectedTooltip.connectionType" />
					</div>
					<core-button theme="blue" text="View game insights" icon="arrow-right" fluid="yes" :spread="true" v-on:click.native.stop="goToGame" class="universe-tooltip-button" />
				</div>

				<!-- Selected tooltip: mobile (slide-up panel at bottom) -->
				<transition name="universe-panel">
					<div class="universe-mobile-panel" v-if="selectedTooltip && $isMobile" v-on:click.stop ref="mobilePanel">
						<button class="universe-tooltip-close" v-on:click.stop="deselectGame"><i class="fa fa-times"></i></button>
						<div class="universe-tooltip-head">
							<div class="universe-tooltip-name">{{ selectedTooltip.name }}</div>
							<div class="universe-tooltip-year" v-if="selectedTooltip.year">{{ selectedTooltip.year }}</div>
						</div>
						<div class="universe-tooltip-metrics">
							<com-overview :label="selectedTooltip.ownLabel" type="date" :value="selectedTooltip.ownedDateRaw" :empty="!selectedTooltip.ownedDateRaw" :inset="true" />
							<com-overview v-if="selectedTooltip.rating" label="Rating" type="rating" :value="selectedTooltip.rating" :inset="true" />
							<com-overview label="Plays" type="number" :value="selectedTooltip.plays" :empty="!selectedTooltip.plays" :inset="true" />
							<com-overview v-if="selectedTooltip.firstPlayedRaw" label="First Played" type="date" :value="selectedTooltip.firstPlayedRaw" :inset="true" />
							<com-overview v-if="selectedTooltip.lastPlayedRaw" label="Last Played" type="date" :value="selectedTooltip.lastPlayedRaw" :inset="true" />
							<com-overview label="Connections" type="number" :value="selectedTooltip.connections" :empty="!selectedTooltip.connections" :inset="true" :sublabel="selectedTooltip.connectionType" />
						</div>
						<core-button theme="blue" text="View game insights" icon="arrow-right" fluid="yes" :spread="true" v-on:click.native.stop="goToGame" class="universe-tooltip-button" />
					</div>
				</transition>

				<!-- Segment/sector tooltip (dark theme) -->
				<div class="universe-segment-tooltip" v-if="tooltip && tooltip.isSegment" :style="tooltipStyle">
					<div class="universe-segment-tooltip-name">{{ tooltip.segOrbit }}</div>
					<div class="universe-segment-tooltip-sub">{{ tooltip.segRating }}</div>
					<div class="universe-segment-tooltip-count">{{ tooltip.segCount }} <span>{{ tooltip.segCount === 1 ? 'game' : 'games' }}</span></div>
					<div class="universe-segment-tooltip-hint">Click to view games</div>
				</div>

			</div>
			</portal>


		</div>

	</template>

</com-item>

</template>

<script>

import comItem from './../common/Item'
import comToggle from './../common/Toggle'
import comOverview from './../common/Overview'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Dot colour per first-touch type
var COLOURS = {
	own: '#4881bb',
	'previously owned': '#7a5fbf',
	played: '#2FC482',
	rated: '#f5a623',
	wishlisted: '#e85d75'
}

var COLOUR_DEFAULT = '#aab2bd'

// Dark space theme
var BG_COLOUR = '#0f1729'
var BG_STAR_COLOUR = 'rgba(255, 255, 255, 0.06)'

// Adaptive orbit sizing limits
var CORE_MIN_PX = 64          // minimum active ring radius in px
// eslint-disable-next-line no-unused-vars
var CORE_MAX = 0.65           // maximum active ring radius (fraction of maxR)
var FRESH_INNER = 0.08        // empty centre before fresh dots start (fraction of maxR)
var ORBIT_GAP = 0.04          // gap between fresh and active bands (fraction of maxR)

// Dot radius range (world units) — scaled by log of play count
var DOT_MIN = 1.5
var DOT_MAX = 5

// Zoom thresholds for transitioning from dots to game thumbnail images
var IMAGE_ZOOM_START = 2.5
var IMAGE_ZOOM_FULL = 5
var IMAGE_PX_MIN = 12
var IMAGE_PX_MAX = 40

// Rating bands — ordered clockwise from top to match fixed crust positions
// Top: 9-10, Top-right: Unrated, Bottom-right: 1-4, Bottom: 5-6, Bottom-left: 7, Top-left: 8
var RATING_BANDS = [
	{ key: 'great', label: '9–10', min: 9, max: 10 },
	{ key: 'unrated', label: 'Unrated', min: 0, max: 0 },
	{ key: 'low', label: '1–4', min: 1, max: 4.999 },
	{ key: 'mid', label: '5–6', min: 5, max: 6.999 },
	{ key: 'seven', label: '7', min: 7, max: 7.999 },
	{ key: 'eight', label: '8', min: 8, max: 8.999 }
]

// Crust band width along canvas edges (world units)
var CRUST_BAND = 20

// Crust fill colour per rating band
var CRUST_COLOURS = {
	great: '#249563',
	unrated: '#7c8696',
	low: '#df4751',
	mid: '#5369a2',
	seven: '#1d8acd',
	eight: '#2FC482'
}

// Maximum concurrent image loads (prevents flooding the browser)
var IMAGE_LOAD_LIMIT = 6

// Pan clamp padding (screen px) — prevents content touching the viewport edge
var PAN_PADDING = (window.innerWidth <= 767) ? 0 : 20

// Maximum zoom level
var MAX_ZOOM = 12

// Connection line styling
var CONNECTION_COLOUR = 'rgba(255, 200, 100, 0.45)'

// Stale positioning: age thresholds for distance mapping
var STALE_THRESHOLD = 730 * 86400  // 2 years in seconds
// eslint-disable-next-line no-unused-vars
var STALE_MAX_AGE = 12 * 365.25 * 86400  // 12 years — beyond this, cluster at edge

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Map a user rating to its band index (0–5), matching RATING_BANDS order
function ratingBand(rating) {

	if (!rating || rating <= 0) return 1  // unrated
	if (rating < 5) return 2              // 1–4
	if (rating < 7) return 3              // 5–6
	if (rating < 8) return 4              // 7
	if (rating < 9) return 5              // 8
	return 0                              // 9–10

}

// Deterministic pseudo-random [0, 1) from a game ID and salt — gives each game
// a stable position that doesn't change between renders
function seeded(id, salt) {

	var x = Math.sin(id * 9301 + (salt === 'b' ? 49297 : 12345)) * 49297
	return x - Math.floor(x)

}

// Format a unix timestamp as "Mon YYYY"
function formatDate(ts) {

	if (!ts) return null
	var d = new Date(ts * 1000)
	var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
	return months[d.getMonth()] + ' ' + d.getFullYear()

}

// Format a unix timestamp as a human-readable age string (e.g. "3 months", "2 years")
function formatAge(ts) {

	if (!ts) return null
	var now = Date.now() / 1000
	var diff = now - ts
	var days = Math.floor(diff / 86400)

	if (days < 1) return 'today'
	if (days === 1) return '1 day'
	if (days < 14) return days + ' days'
	var weeks = Math.floor(days / 7)
	if (weeks < 9) return weeks + ' weeks'
	var months = Math.floor(days / 30.44)
	if (months < 24) return months + ' months'
	var years = Math.floor(days / 365.25)
	return years + ' years'

}

// Darken a hex colour by a fraction (0–1). E.g. darkenHex('#ff8800', 0.2) → 20% darker.
function darkenHex(hex, amount) {

	var r = parseInt(hex.slice(1, 3), 16)
	var g = parseInt(hex.slice(3, 5), 16)
	var b = parseInt(hex.slice(5, 7), 16)
	var m = 1 - amount
	return '#' + (
		(1 << 24) +
		(Math.round(r * m) << 16) +
		(Math.round(g * m) << 8) +
		Math.round(b * m)
	).toString(16).slice(1)

}

// Find where a ray from the origin at `angle` hits the canvas rectangle edge.
// halfW/halfH are half the canvas dimensions in world units.
// Returns { x, y, dist } where dist is the distance from origin to the edge point.
function canvasEdgePoint(angle, halfW, halfH) {

	var cos = Math.cos(angle)
	var sin = Math.sin(angle)

	// Find intersection with each edge and pick the nearest
	var t = Infinity

	if (cos > 0.0001) { var tx = halfW / cos; if (tx > 0 && tx < t) t = tx }
	if (cos < -0.0001) { var txn = -halfW / cos; if (txn > 0 && txn < t) t = txn }
	if (sin > 0.0001) { var ty = halfH / sin; if (ty > 0 && ty < t) t = ty }
	if (sin < -0.0001) { var tyn = -halfH / sin; if (tyn > 0 && tyn < t) t = tyn }

	return { x: cos * t, y: sin * t, dist: t }

}

// Convert a world coordinate near the canvas edge to a perimeter position
// (distance from top-centre, clockwise). Used for crust band hit-testing.
function worldToPerimeterPos(wx, wy, hw, hh) {

	var dTop = Math.abs(wy + hh)
	var dRight = Math.abs(wx - hw)
	var dBottom = Math.abs(wy - hh)
	var dLeft = Math.abs(wx + hw)
	var minDist = Math.min(dTop, dRight, dBottom, dLeft)
	var perim = 4 * hw + 4 * hh

	if (minDist === dTop) {
		// Top edge: x=0 → pos 0, x=hw → pos hw, x=-hw → wraps near end
		return wx >= 0 ? wx : perim + wx
	}
	if (minDist === dRight) {
		// Right edge: y=-hh → pos hw, y=hh → pos hw+2*hh
		return hw + (wy + hh)
	}
	if (minDist === dBottom) {
		// Bottom edge: x=hw → pos hw+2*hh, x=-hw → pos 3*hw+2*hh
		return hw + 2 * hh + (hw - wx)
	}
	// Left edge: y=hh → pos 3*hw+2*hh, y=-hh → pos 3*hw+4*hh
	return 3 * hw + 2 * hh + (hh - wy)

}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default {

	props: ['fetching', 'your', 'you', 'size'],

	components: {
		'com-item': comItem,
		'com-toggle': comToggle,
		'com-overview': comOverview
	},

	data: function() {

		return {

			// Computed node positions (world coords) — rebuilt on data change / resize
			nodes: [],

			// Computed sector angular ranges — rebuilt on data change / resize
			sectors: [],

			// Adaptive orbit radii (fractions of maxR) — computed in layout()
			orbitBands: { f: [0.08, 0.28], a: [0.34, 0.60] },
			orbitRing: { f: 0.30, a: 0.64 },
			orbitSegments: [
				{ key: 'f', inner: 0, outer: 0.30 },
				{ key: 'a', inner: 0.30, outer: 0.64 }
			],

			// Crust band perimeter ranges — rebuilt on render, used for hit-testing
			crustRanges: [],

			// Canvas dimensions (CSS px, not device px)
			canvasWidth: 0,
			canvasHeight: 0,

			// Half canvas in world units (canvas centre = world origin)
			halfW: 0,
			halfH: 0,

			// Maximum plot radius (px) — derived from canvas size (for fresh + active rings)
			maxR: 0,

			// Adaptive image zoom thresholds (computed in layout based on density)
			imageZoomStart: IMAGE_ZOOM_START,
			imageZoomFull: IMAGE_ZOOM_FULL,
			maxZoom: MAX_ZOOM,

			// Zoom / pan state
			zoom: 1,
			panX: 0,
			panY: 0,

			// Drag-to-pan tracking
			isPanning: false,
			panStartX: 0,
			panStartY: 0,
			panStartPanX: 0,
			panStartPanY: 0,
			hasPanned: false,

			// Hover state
			hovered: null,
			hoveredSector: null,
			hoveredSegment: null,
			hoverProgress: 0,
			hoverAnimating: false,

			// Click selection state — selected game dot persists connections + tooltip
			selectedGame: null,

			// Intro fly-out animation (0→1 over ~1.5s on first layout)
			introProgress: 0,
			introAnimating: false,

			// Static star field positions (generated once in layout)
			stars: [],

			// Tooltip (hover)
			tooltip: null,
			tooltipX: 0,
			tooltipY: 0,
			tooltipStyle: {},
			tooltipCorner: 'tl',

			// Tooltip (selected game — persists independently)
			selectedTooltip: null,
			selectedTooltipX: 0,
			selectedTooltipY: 0,
			selectedTooltipStyle: {},
			selectedTooltipCorner: 'tl',

			// Fullscreen state
			isFullscreen: false,

			// Touch state
			touchStartTime: 0,
			touchStartX: 0,
			touchStartY: 0,
			touchMoved: false,
			pinchStartDist: 0,
			pinchStartZoom: 1

		}

	},

	// ---------------------------------------------------------------------------
	// Computed
	// ---------------------------------------------------------------------------

	computed: {

		// True while the initial full data load is in progress
		loading: function() {

			return this.fetching === 'all'

		},

		// Raw universe data from the page store (orbits + games array)
		data: function() {

			return this.$store.getters['page/insights/games/universe']

		},

		// Game reference data keyed by ID (for resolving names in tooltips)
		games: function() {

			return this.$store.getters['data/games/associated']

		},

		// Whether the visualisation has data and is ready to display
		active: function() {

			return this.data && this.data.orbits && !this.loading

		},

		// Orbit counts from the backend
		orbits: function() {

			return (this.data && this.data.orbits) ? this.data.orbits : { fresh: 0, active: 0, stale: 0 }

		},

		totalGames: function() {

			return this.orbits.fresh + this.orbits.active + this.orbits.stale

		},

		// Current link type from Vuex filter
		linkType: function() {

			return this.$store.getters['filter/insights/games/universe.links']

		},

		// Dropdown options for connection type
		linkOptions: function() {

			return [
				{ value: 'd', text: 'Designer connections' },
				{ value: 'm', text: 'Mechanic connections' },
				{ value: 'p', text: 'Publisher connections' },
				{ value: 'c', text: 'Category connections' }
			]

		},



	},

	// ---------------------------------------------------------------------------
	// Watchers
	// ---------------------------------------------------------------------------

	watch: {

		// Re-layout when data changes (initial load or segment filter response)
		data: function() {

			this.selectedGame = null
			this.tooltip = null
			this.selectedTooltip = null
			this.$nextTick(this.layout)

		}

	},

	// ---------------------------------------------------------------------------
	// Lifecycle
	// ---------------------------------------------------------------------------

	mounted: function() {

		this.imageCache = {}
		this.imageLoading = 0
		this.renderTimeout = null
		this.connectionIndex = {}  // game_id → { connections: Set<game_id>, designers: Set<game_id>, mechanics: Set<game_id> }
		this.nodeByGame = {}       // game_id → node index

		this.resizeHandler = this.onResize.bind(this)
		this.mouseMoveHandler = this.onGlobalMouseMove.bind(this)
		this.mouseUpHandler = this.onGlobalMouseUp.bind(this)
		this.keyDownHandler = this.onKeyDown.bind(this)

		window.addEventListener('resize', this.resizeHandler)
		document.addEventListener('keydown', this.keyDownHandler)

		this.$nextTick(this.layout)

	},

	beforeDestroy: function() {

		window.removeEventListener('resize', this.resizeHandler)
		document.removeEventListener('mousemove', this.mouseMoveHandler)
		document.removeEventListener('mouseup', this.mouseUpHandler)
		document.removeEventListener('keydown', this.keyDownHandler)
		document.body.classList.remove('universe-fullscreen-active')

	},

	// ---------------------------------------------------------------------------
	// Methods
	// ---------------------------------------------------------------------------

	methods: {

		// -------------------------------------------------------------------
		// Layout — converts raw game data into positioned nodes and sectors
		// -------------------------------------------------------------------

		layout: function() {

			if (!this.data || !this.data.games || !this.$refs.wrapper) return

			var wrapper = this.$refs.wrapper
			var canvas = this.$refs.canvas
			var dpr = window.devicePixelRatio || 1

			// Size the canvas — fullscreen fills the viewport, normal mode is aspect-constrained
			var width, height
			if (this.isFullscreen) {
				width = window.innerWidth
				height = window.innerHeight
			} else {
				width = wrapper.clientWidth
				height = Math.max(400, Math.min(640, width * 0.65))
			}

			this.canvasWidth = width
			this.canvasHeight = height
			this.halfW = width / 2
			this.halfH = height / 2

			canvas.width = width * dpr
			canvas.height = height * dpr
			canvas.style.width = width + 'px'
			canvas.style.height = height + 'px'

			// maxR sizes the fresh+active circular core — smaller relative to canvas
			// so there is ample space for the stale scatter
			this.maxR = Math.min(width, height) * 0.25

			var games = this.data.games
			var now = Date.now() / 1000

			// ----- Sector computation -----
			// Fixed angular sectors aligned to the 6 crust positions.
			// Boundaries point from centre to the perimeter midpoints between adjacent anchors.

			var bandCounts = [0, 0, 0, 0, 0, 0]
			for (var c = 0; c < games.length; c++) {
				bandCounts[ratingBand(games[c].r)]++
			}

			// Boundary angles: direction from centre toward midpoint between adjacent anchors
			var hw = this.halfW
			var hh = this.halfH
			var b01 = Math.atan2(-hh, hw / 2)              // top ↔ TR
			var b12 = 0                                      // TR ↔ BR (right centre)
			var b23 = Math.atan2(hh, hw / 2)                // BR ↔ bottom
			var b34 = Math.atan2(hh, -hw / 2)               // bottom ↔ BL
			var b45 = Math.PI                                // BL ↔ TL (left centre)
			var b50 = Math.atan2(-hh, -hw / 2) + Math.PI * 2 // TL ↔ top (normalised > PI)
			var bounds = [b01, b12, b23, b34, b45, b50]

			// Build sectors: each runs from bounds[i-1] to bounds[i]
			// Sector 0 (top) wraps: from b50 to b01+2π
			var sectors = []
			for (var s = 0; s < 6; s++) {
				var sStart = (s === 0) ? bounds[5] : bounds[s - 1]
				var sEnd = (s === 0) ? bounds[0] + Math.PI * 2 : bounds[s]
				sectors.push({
					startAngle: sStart,
					endAngle: sEnd,
					fraction: (sEnd - sStart) / (Math.PI * 2),
					count: bandCounts[s],
					key: RATING_BANDS[s].key,
					label: RATING_BANDS[s].label
				})
			}

			this.sectors = sectors

			// ----- Adaptive orbit sizing -----
			// Scale fresh + active rings based on density (minimum area per dot)

			var freshCount = 0
			var activeCount = 0
			var staleCount = 0 // eslint-disable-line no-unused-vars
			for (var oc = 0; oc < games.length; oc++) {
				if (games[oc].o === 'f') freshCount++
				else if (games[oc].o === 'a') activeCount++
				else staleCount++
			}

			var total = games.length || 1
			var coreCount = freshCount + activeCount

			// Minimum active band width in maxR fraction (64px minimum)
			var activeBandMin = CORE_MIN_PX / this.maxR

			// Density-based sizing: ensure each dot has enough breathing room
			// Target area per dot (in maxR² units) — tuned for readable spacing
			var areaPerDot = 0.0025
			var minFreshArea = freshCount * areaPerDot
			var minActiveArea = activeCount * areaPerDot

			// Fresh ring: sized by density, clamped to reasonable bounds
			var densityFreshR = Math.sqrt(minFreshArea / Math.PI + FRESH_INNER * FRESH_INNER)
			var freshFraction = coreCount > 0 ? freshCount / coreCount : 0.3
			var baseFreshR = 0.20 * Math.sqrt(freshFraction / 0.3)
			var freshR = Math.max(FRESH_INNER + 0.04, Math.min(Math.max(baseFreshR, densityFreshR), 0.40))

			// Active band: sized by density, with minimum pixel width
			var densityActiveBand = Math.sqrt((minActiveArea / Math.PI) + freshR * freshR) - freshR
			var coreFraction = coreCount / total
			var activeBandTarget = activeBandMin + (0.30 - activeBandMin) * Math.sqrt(coreFraction)
			var activeBand = Math.max(activeBandMin, Math.min(Math.max(activeBandTarget, densityActiveBand), 0.35))
			var coreR = freshR + ORBIT_GAP + activeBand
			if (coreR > 0.75) {
				// Shrink proportionally but never below minimum
				var excess = coreR - 0.75
				var freshShrink = Math.min(excess * 0.5, freshR - FRESH_INNER - 0.04)
				freshR -= freshShrink
				activeBand = Math.max(activeBandMin, 0.75 - freshR - ORBIT_GAP)
				coreR = freshR + ORBIT_GAP + activeBand
			}

			var activeBandInner = freshR + ORBIT_GAP
			var activeBandOuter = coreR - 0.02

			this.orbitRing = { f: freshR, a: coreR }
			this.orbitBands = {
				f: [FRESH_INNER, Math.max(FRESH_INNER + 0.02, freshR - 0.02)],
				a: [activeBandInner, activeBandOuter]
			}
			this.orbitSegments = [
				{ key: 'f', inner: 0, outer: freshR },
				{ key: 'a', inner: freshR, outer: coreR }
			]

			// ----- Node computation -----
			// Position each game dot:
			//   Fresh + Active: polar coords within orbit bands
			//   Stale: scatter across rectangular canvas, distance from centre = staleness

			// Pre-scan stale games for actual age range (even distribution)
			var staleMinAge = Infinity
			var staleMaxAge = 0
			for (var qi = 0; qi < games.length; qi++) {
				if (games[qi].o === 's') {
					var sAge = games[qi].ld ? (now - games[qi].ld) : 0
					if (sAge < staleMinAge) staleMinAge = sAge
					if (sAge > staleMaxAge) staleMaxAge = sAge
				}
			}
			// Fallback if no stale games or all same age
			if (staleMinAge === Infinity) staleMinAge = STALE_THRESHOLD
			if (staleMaxAge <= staleMinAge) staleMaxAge = staleMinAge + 1

			var maxPlays = 1
			for (var p = 0; p < games.length; p++) {
				if (games[p].p > maxPlays) maxPlays = games[p].p
			}
			var logMax = Math.log(maxPlays + 1)

			var activeRingR = this.orbitRing.a * this.maxR
			var nodes = []

			for (var i = 0; i < games.length; i++) {

				var g = games[i]
				var bi = ratingBand(g.r)
				var sector = sectors[bi]

				// Three seeded randoms per game: angle, radius, tangential
				var seedA = seeded(g.g, 'a')
				var seedB = seeded(g.g, 'b')
				var seedC = seeded(g.g, 'c')

				// Angle within sector (with padding from edges to avoid clumping at boundaries)
				var pad = 0.02
				var sectorRange = sector.endAngle - sector.startAngle
				var angle = sector.startAngle + pad * sectorRange + seedA * sectorRange * (1 - 2 * pad)

				var wx, wy

				if (g.o === 'f' || g.o === 'a') {

					// Fresh + Active: circular orbit bands
					var band = this.orbitBands[g.o]
					var radius = (band[0] + (band[1] - band[0]) * seedB) * this.maxR
					wx = Math.cos(angle) * radius
					wy = Math.sin(angle) * radius

				} else {

					// Stale: scatter across rectangular canvas, distance correlates to age
					// Use a second seeded angle for extra spread within the sector
					var staleAngle = sector.startAngle + pad * sectorRange + seedC * sectorRange * (1 - 2 * pad)

					// Blend: base angle (from seedA) + spread angle (from seedC) weighted by distance
					// Near active ring, stick closer to base angle; far out, spread more
					var edgePt = canvasEdgePoint(angle, this.halfW, this.halfH)
					var maxDist = edgePt.dist - CRUST_BAND - 2

					// Map staleness to distance — use actual data range for even distribution
					var age = g.ld ? (now - g.ld) : staleMaxAge
					var staleness = (staleMaxAge > staleMinAge) ? (age - staleMinAge) / (staleMaxAge - staleMinAge) : 0.5
					if (staleness < 0) staleness = 0
					if (staleness > 1) staleness = 1

					var staleRange = maxDist - activeRingR - 4
					var baseDist = activeRingR + 4 + staleness * staleRange * 0.80

					// Radial jitter to break up arcs
					var radialJitter = (seedB - 0.5) * staleRange * 0.25
					var dist = baseDist + radialJitter
					if (dist < activeRingR + 2) dist = activeRingR + 2
					if (dist > maxDist) dist = maxDist

					// Blend angle: near active ring use base angle, further out mix in spread angle
					var distFraction = (dist - activeRingR) / (maxDist - activeRingR)
					var blendedAngle = angle + (staleAngle - angle) * distFraction * 0.6

					// Recompute edge for blended angle to get correct maxDist
					var blendEdge = canvasEdgePoint(blendedAngle, this.halfW, this.halfH)
					var blendMax = blendEdge.dist - CRUST_BAND - 2
					if (dist > blendMax) dist = blendMax

					wx = Math.cos(blendedAngle) * dist
					wy = Math.sin(blendedAngle) * dist

				}

				var playFraction = (g.p > 0) ? Math.log(g.p + 1) / logMax : 0
				var dotR = DOT_MIN + playFraction * (DOT_MAX - DOT_MIN)

				nodes.push({
					wx: wx,
					wy: wy,
					r: dotR,
					colour: COLOURS[g.ft] || COLOUR_DEFAULT,
					game: g
				})

			}

			// ----- Collision nudging (grid-accelerated) -----
			// Push overlapping dots apart (3 passes, spatial grid for O(n) per pass)
			var nudgeGap = 4  // minimum gap between dot edges (world units)
			var cellSize = DOT_MAX * 2 + nudgeGap + 2
			for (var pass = 0; pass < 3; pass++) {
				// Build spatial grid
				var grid = {}
				for (var ngi = 0; ngi < nodes.length; ngi++) {
					var ngx = Math.floor(nodes[ngi].wx / cellSize)
					var ngy = Math.floor(nodes[ngi].wy / cellSize)
					var ngk = ngx + ',' + ngy
					if (!grid[ngk]) grid[ngk] = []
					grid[ngk].push(ngi)
				}
				// Check neighbours in adjacent cells
				for (var nkey in grid) {
					var ncell = grid[nkey]
					var nparts = nkey.split(',')
					var ncx = parseInt(nparts[0])
					var ncy = parseInt(nparts[1])
					// Collect indices from this cell and 8 neighbours
					var nearby = []
					for (var ox = -1; ox <= 1; ox++) {
						for (var oy = -1; oy <= 1; oy++) {
							var adjk = (ncx + ox) + ',' + (ncy + oy)
							if (grid[adjk]) nearby = nearby.concat(grid[adjk])
						}
					}
					for (var ca = 0; ca < ncell.length; ca++) {
						var cai = ncell[ca]
						for (var cb = 0; cb < nearby.length; cb++) {
							var cbi = nearby[cb]
							if (cbi <= cai) continue
							var ndx = nodes[cbi].wx - nodes[cai].wx
							var ndy = nodes[cbi].wy - nodes[cai].wy
							var nd2 = ndx * ndx + ndy * ndy
							var minDist = nudgeGap + nodes[cai].r + nodes[cbi].r
							if (nd2 < minDist * minDist && nd2 > 0) {
								var nd = Math.sqrt(nd2)
								var noverlap = (minDist - nd) * 0.5
								var nux = ndx / nd
								var nuy = ndy / nd
								nodes[cai].wx -= nux * noverlap
								nodes[cai].wy -= nuy * noverlap
								nodes[cbi].wx += nux * noverlap
								nodes[cbi].wy += nuy * noverlap
							}
						}
					}
				}
			}

			// Clamp nudged nodes back within canvas bounds (respect crust)
			var clampW = this.halfW - CRUST_BAND - 2
			var clampH = this.halfH - CRUST_BAND - 2
			for (var nc = 0; nc < nodes.length; nc++) {
				if (nodes[nc].wx < -clampW) nodes[nc].wx = -clampW
				if (nodes[nc].wx > clampW) nodes[nc].wx = clampW
				if (nodes[nc].wy < -clampH) nodes[nc].wy = -clampH
				if (nodes[nc].wy > clampH) nodes[nc].wy = clampH
			}

			// ----- Per-node nearest-neighbour distance (grid-accelerated) -----
			// Used to cap image size so thumbnails never overlap
			var nnCell = 30  // grid cell size for neighbour search
			var nnGrid = {}
			for (var nni = 0; nni < nodes.length; nni++) {
				var nnkx = Math.floor(nodes[nni].wx / nnCell)
				var nnky = Math.floor(nodes[nni].wy / nnCell)
				var nnk = nnkx + ',' + nnky
				if (!nnGrid[nnk]) nnGrid[nnk] = []
				nnGrid[nnk].push(nni)
				nodes[nni].nn = nnCell  // default (will be overwritten)
			}
			for (var nnj = 0; nnj < nodes.length; nnj++) {
				var nnx = Math.floor(nodes[nnj].wx / nnCell)
				var nny = Math.floor(nodes[nnj].wy / nnCell)
				var closest = nnCell
				for (var nnox = -1; nnox <= 1; nnox++) {
					for (var nnoy = -1; nnoy <= 1; nnoy++) {
						var nnbk = (nnx + nnox) + ',' + (nny + nnoy)
						var nnbucket = nnGrid[nnbk]
						if (!nnbucket) continue
						for (var nnb = 0; nnb < nnbucket.length; nnb++) {
							var nnbi = nnbucket[nnb]
							if (nnbi === nnj) continue
							var nndx = nodes[nnbi].wx - nodes[nnj].wx
							var nndy = nodes[nnbi].wy - nodes[nnj].wy
							var nndd = Math.sqrt(nndx * nndx + nndy * nndy)
							if (nndd < closest) closest = nndd
						}
					}
				}
				nodes[nnj].nn = closest
			}

			// ----- Adaptive image zoom threshold -----
			// When dots are dense, require more zoom before switching to thumbnails
			var totalArea = (this.halfW * 2) * (this.halfH * 2)
			var dotDensity = nodes.length / (totalArea || 1)
			// Base threshold at 2.5; scale up with density (more dots = need more zoom)
			var densityScale = Math.max(1, dotDensity * 800)
			this.imageZoomStart = IMAGE_ZOOM_START * Math.min(densityScale, 3)
			this.imageZoomFull = this.imageZoomStart + (IMAGE_ZOOM_FULL - IMAGE_ZOOM_START)
			// Ensure enough zoom headroom beyond image threshold for full-size thumbnails
			this.maxZoom = Math.max(MAX_ZOOM, this.imageZoomFull * 3)

			this.nodes = nodes

			// Build node-by-game lookup (game_id → node index)
			var nodeByGame = {}
			for (var ni = 0; ni < nodes.length; ni++) {
				nodeByGame[nodes[ni].game.g] = ni
			}
			this.nodeByGame = nodeByGame

			// Build connection index from backend links data (single type, flat map)
			// connectionIndex[game_id] = { nodeIndex: 1, ... }
			var connectionIndex = {}
			var links = (this.data && this.data.links) ? this.data.links : {}

			for (var lid in links) {
				var lGames = links[lid]
				for (var la = 0; la < lGames.length; la++) {
					var laGid = lGames[la]
					if (nodeByGame[laGid] === undefined) continue
					if (!connectionIndex[laGid]) connectionIndex[laGid] = {}
					for (var lb = 0; lb < lGames.length; lb++) {
						var lbGid = lGames[lb]
						if (lbGid === laGid || nodeByGame[lbGid] === undefined) continue
						connectionIndex[laGid][nodeByGame[lbGid]] = 1
					}
				}
			}

			this.connectionIndex = connectionIndex

			// Generate star field (once — stable across re-layouts)
			if (!this.stars.length) {
				var starCount = 200
				var starList = []
				for (var si = 0; si < starCount; si++) {
					starList.push({
						x: (seeded(si + 1000, 'a') - 0.5) * 2 * this.halfW,
						y: (seeded(si + 1000, 'b') - 0.5) * 2 * this.halfH,
						s: 0.5 + seeded(si + 1000, 'c') * 1.0
					})
				}
				this.stars = starList
			}

			// Trigger intro fly-out on first layout
			if (!this.introAnimating && this.introProgress === 0 && nodes.length) {
				this.introAnimating = true
				this.animateIntro()
			} else {
				this.render()
			}

		},

		// -------------------------------------------------------------------
		// Coordinate transforms
		// -------------------------------------------------------------------

		// Constrain pan so the viewport never scrolls past the canvas bounds
		clampPan: function() {

			var halfViewW = this.canvasWidth / 2 / this.zoom
			var halfViewH = this.canvasHeight / 2 / this.zoom
			var padding = PAN_PADDING / this.zoom
			var maxPanX = Math.max(0, this.halfW + padding - halfViewW)
			var maxPanY = Math.max(0, this.halfH + padding - halfViewH)

			this.panX = Math.max(-maxPanX, Math.min(maxPanX, this.panX))
			this.panY = Math.max(-maxPanY, Math.min(maxPanY, this.panY))

		},

		// World (0,0 = universe centre) → screen (CSS px)
		worldToScreen: function(wx, wy) {

			return {
				x: (wx - this.panX) * this.zoom + this.canvasWidth / 2,
				y: (wy - this.panY) * this.zoom + this.canvasHeight / 2
			}

		},

		// Screen (CSS px) → world
		screenToWorld: function(sx, sy) {

			return {
				x: (sx - this.canvasWidth / 2) / this.zoom + this.panX,
				y: (sy - this.canvasHeight / 2) / this.zoom + this.panY
			}

		},

		// -------------------------------------------------------------------
		// Render — draws the entire canvas each frame
		// -------------------------------------------------------------------

		render: function() {

			var canvas = this.$refs.canvas
			if (!canvas) return
			var ctx = canvas.getContext('2d')
			var dpr = window.devicePixelRatio || 1
			var zoom = this.zoom
			var hoveredIndex = this.hovered
			var showImages = zoom >= this.imageZoomStart

			// Reset transform and fill dark background
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			ctx.fillStyle = BG_COLOUR
			ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

			// Apply zoom/pan: translate to centre, scale, then offset by pan
			ctx.save()
			ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2)
			ctx.scale(zoom, zoom)
			ctx.translate(-this.panX, -this.panY)

			// Star field background
			ctx.fillStyle = BG_STAR_COLOUR
			for (var si = 0; si < this.stars.length; si++) {
				var star = this.stars[si]
				ctx.beginPath()
				ctx.arc(star.x, star.y, star.s / zoom, 0, Math.PI * 2)
				ctx.fill()
			}

			var hovSector = this.hoveredSector

			// ----- Edge crust bands (coloured rating sectors along canvas edges) -----
			this.renderCrust(ctx, hovSector, zoom)

			// ----- Sector lines from centre to canvas edges -----
			this.renderSectorLines(ctx, zoom)

			// ----- Selection and hover highlights -----
			this.renderHighlights(ctx, zoom)

			// ----- Orbit ring circles + labels -----
			this.renderOrbits(ctx, zoom)

			// ----- Image transition factor (0 at zoom start, 1 at zoom full) -----
			var imageFactor = showImages ? Math.min(1, (zoom - this.imageZoomStart) / (this.imageZoomFull - this.imageZoomStart)) : 0
			var imgScreenPx = IMAGE_PX_MIN + imageFactor * (IMAGE_PX_MAX - IMAGE_PX_MIN)
			var imgWorldSize = imgScreenPx / zoom

			// Visible viewport in world coords (for off-screen culling)
			var viewLeft = this.panX - this.canvasWidth / 2 / zoom
			var viewRight = this.panX + this.canvasWidth / 2 / zoom
			var viewTop = this.panY - this.canvasHeight / 2 / zoom
			var viewBottom = this.panY + this.canvasHeight / 2 / zoom

			// ----- Connection data for hovered or selected dot (desktop/fullscreen only) -----
			var connectedSet = null
			var connectionSource = (this.selectedGame !== null) ? this.selectedGame : hoveredIndex
			if (connectionSource !== null && this.nodes[connectionSource] && (!this.$isMobile || this.isFullscreen)) {
				var connGame = this.nodes[connectionSource].game
				var connEntry = this.connectionIndex[connGame.g]
				if (connEntry && Object.keys(connEntry).length) {
					connectedSet = connEntry
				}
			}

			// ----- Game dots (dimmed if connections shown) -----
			this.renderDots(ctx, zoom, hoveredIndex, imageFactor, imgWorldSize, viewLeft, viewRight, viewTop, viewBottom, connectedSet)

			// ----- Connection lines (drawn over dimmed dots, under highlighted dots) -----
			if (connectionSource !== null && connectedSet) {
				this.renderConnections(ctx, zoom, connectionSource, connectedSet)
			}

			// ----- Connected dots drawn on top (bright, over connection lines) -----
			if (connectionSource !== null && connectedSet) {
				this.renderConnectedDots(ctx, zoom, connectionSource, connectedSet, imageFactor, imgWorldSize, viewLeft, viewRight, viewTop, viewBottom)
			}

			// ----- Selected dot drawn prominently (persistent selection) -----
			if (this.selectedGame !== null && this.nodes[this.selectedGame]) {
				this.renderSelectedDot(ctx, zoom, this.nodes[this.selectedGame], imageFactor, imgWorldSize)
			}

			// ----- Hover effect on hovered dot -----
			if (hoveredIndex !== null && hoveredIndex !== this.selectedGame && this.nodes[hoveredIndex]) {
				this.renderHoverEffect(ctx, zoom, this.nodes[hoveredIndex], imageFactor, imgWorldSize, this.selectedGame === null)
			}

			ctx.restore()

		},

		// Draw coloured rating bands filling the canvas perimeter.
		// 6 bands meet at midpoints between anchors — symmetrical: top/bottom (hw each), 4 corners (hw/2+hh each).
		renderCrust: function(ctx, hovSector, zoom) {

			var hw = this.halfW
			var hh = this.halfH
			var band = CRUST_BAND / zoom
			var perim = 4 * hw + 4 * hh

			// Perimeter midpoints between adjacent anchors (clockwise from top↔TR)
			// These are the boundaries where crust bands meet.
			var mid = [
				hw / 2,                     // 0: top ↔ TR
				hw + hh,                    // 1: TR ↔ BR
				3 * hw / 2 + 2 * hh,       // 2: BR ↔ bottom
				5 * hw / 2 + 2 * hh,       // 3: bottom ↔ BL
				3 * hw + 3 * hh,            // 4: BL ↔ TL
				7 * hw / 2 + 4 * hh         // 5: TL ↔ top (wraps past perim origin)
			]

			// Each band runs from mid[i-1] to mid[i]
			var crustRanges = []

			for (var s = 0; s < this.sectors.length; s++) {
				var sector = this.sectors[s]

				var startPos = (s === 0) ? mid[5] : mid[s - 1]
				var endPos = mid[s]
				var segLen = endPos - startPos
				if (segLen <= 0) segLen += perim

				if (sector.count === 0) {
					crustRanges.push(null)
					continue
				}

				// Normalise startPos into [0, perim)
				var normStart = startPos
				while (normStart >= perim) normStart -= perim
				while (normStart < 0) normStart += perim

				crustRanges.push({ start: normStart, length: segLen })

				var isHov = (hovSector === s)
				var crustW = band

				ctx.globalAlpha = 1
				var baseColour = CRUST_COLOURS[sector.key] || '#ccc'
				ctx.fillStyle = isHov ? darkenHex(baseColour, 0.2) : baseColour

				// Walk along perimeter, drawing rectangles for this band
				var remaining = segLen
				var cursor = normStart

				while (remaining > 0.5) {
					var nc = cursor
					while (nc >= perim) nc -= perim
					var edge = this.perimeterEdge(nc, hw, hh)
					var draw = Math.min(remaining, edge.remaining)
					this.drawCrustRect(ctx, edge, draw, crustW, hw, hh)
					remaining -= draw
					cursor += draw
				}

				// Divider line at band start
				var divEdge = this.perimeterEdge(normStart, hw, hh)
				ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)'
				ctx.lineWidth = 1 / zoom
				ctx.beginPath()
				if (divEdge.side === 'top') {
					ctx.moveTo(divEdge.x, -hh)
					ctx.lineTo(divEdge.x, -hh + crustW)
				} else if (divEdge.side === 'right') {
					ctx.moveTo(hw, divEdge.y)
					ctx.lineTo(hw - crustW, divEdge.y)
				} else if (divEdge.side === 'bottom') {
					ctx.moveTo(divEdge.x, hh)
					ctx.lineTo(divEdge.x, hh - crustW)
				} else {
					ctx.moveTo(-hw, divEdge.y)
					ctx.lineTo(-hw + crustW, divEdge.y)
				}
				ctx.stroke()

				// Label — always horizontal and right-way-up
				var labelSize = Math.max(8, 11 / zoom)

				if (segLen * zoom > 30) {

					ctx.font = '600 ' + labelSize + 'px -apple-system, BlinkMacSystemFont, sans-serif'
					ctx.fillStyle = '#fff'
					ctx.textBaseline = 'middle'

					var lx, ly, align
					var pad = 8 / zoom

					if (s === 0) {
						// Top centre
						lx = 0; ly = -hh + crustW * 0.5; align = 'center'
					} else if (s === 1) {
						// Top-right corner
						lx = hw - pad; ly = -hh + crustW * 0.5; align = 'right'
					} else if (s === 2) {
						// Bottom-right corner
						lx = hw - pad; ly = hh - crustW * 0.5; align = 'right'
					} else if (s === 3) {
						// Bottom centre
						lx = 0; ly = hh - crustW * 0.5; align = 'center'
					} else if (s === 4) {
						// Bottom-left corner
						lx = -hw + pad; ly = hh - crustW * 0.5; align = 'left'
					} else {
						// Top-left corner
						lx = -hw + pad; ly = -hh + crustW * 0.5; align = 'left'
					}

					ctx.textAlign = align
					ctx.fillText(sector.label, lx, ly)

				}

			}

			this.crustRanges = crustRanges
			ctx.globalAlpha = 1

		},

		// Given a distance along the perimeter (starting from top-centre, clockwise),
		// return which edge we're on, the x/y position, and how much edge remains.
		perimeterEdge: function(pos, hw, hh) {

			var w = hw * 2
			var h = hh * 2

			// Edge 1: top, from centre-right (0, -hh) to right (hw, -hh) = hw length
			if (pos < hw) {
				return { side: 'top', x: pos, y: -hh, remaining: hw - pos }
			}
			pos -= hw

			// Edge 2: right, from (hw, -hh) to (hw, hh) = h length
			if (pos < h) {
				return { side: 'right', x: hw, y: -hh + pos, remaining: h - pos }
			}
			pos -= h

			// Edge 3: bottom, from (hw, hh) to (-hw, hh) = w length
			if (pos < w) {
				return { side: 'bottom', x: hw - pos, y: hh, remaining: w - pos }
			}
			pos -= w

			// Edge 4: left, from (-hw, hh) to (-hw, -hh) = h length
			if (pos < h) {
				return { side: 'left', x: -hw, y: hh - pos, remaining: h - pos }
			}
			pos -= h

			// Edge 5 (wrap): top, from (-hw, -hh) to (0, -hh) = hw length
			return { side: 'top', x: -hw + pos, y: -hh, remaining: hw - pos }

		},

		// Draw a rectangle strip along a canvas edge for a portion of the crust
		drawCrustRect: function(ctx, edge, length, crustW, hw, hh) {

			if (edge.side === 'top') {
				ctx.fillRect(edge.x, -hh, length, crustW)
			} else if (edge.side === 'right') {
				ctx.fillRect(hw - crustW, edge.y, crustW, length)
			} else if (edge.side === 'bottom') {
				ctx.fillRect(edge.x - length, hh - crustW, length, crustW)
			} else {
				ctx.fillRect(-hw, edge.y - length, crustW, length)
			}

		},

		// Draw faint radial lines from the centre to the inside edge of the crust band
		renderSectorLines: function(ctx, zoom) {

			ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)'
			ctx.lineWidth = 1 / zoom

			var crustW = CRUST_BAND / zoom
			var innerHW = this.halfW - crustW
			var innerHH = this.halfH - crustW

			for (var s = 0; s < this.sectors.length; s++) {
				var sc = this.sectors[s]
				if (sc.count === 0) continue
				var edge = canvasEdgePoint(sc.startAngle, innerHW, innerHH)
				ctx.beginPath()
				ctx.moveTo(0, 0)
				ctx.lineTo(edge.x, edge.y)
				ctx.stroke()
			}

		},

		// Draw hover highlights for segments and crust
		renderHighlights: function(ctx, zoom) {

			var activeRingR = this.orbitRing.a * this.maxR

			if (this.hoveredSector !== null && this.hoveredSegment === null) {

				// Crust hover — highlight the full sector wedge (all orbits)
				var hovSec = this.sectors[this.hoveredSector]
				if (hovSec && hovSec.count > 0) {
					// Full wedge from centre to canvas edge
					var steps = 20
					ctx.beginPath()
					ctx.moveTo(0, 0)
					for (var wi = 0; wi <= steps; wi++) {
						var wa = hovSec.startAngle + (hovSec.endAngle - hovSec.startAngle) * (wi / steps)
						var wep = canvasEdgePoint(wa, this.halfW, this.halfH)
						ctx.lineTo(wep.x, wep.y)
					}
					ctx.closePath()
					ctx.fillStyle = 'rgba(100, 160, 255, 0.08)'
					ctx.fill()
					ctx.strokeStyle = 'rgba(100, 160, 255, 0.2)'
					ctx.lineWidth = 1 / zoom
					ctx.stroke()
				}

			} else if (this.hoveredSegment !== null) {

				// Segment hover — highlight just the orbit × sector intersection
				var segSector = this.sectors[this.hoveredSegment.sector]
				if (segSector && segSector.count > 0) {

					if (this.hoveredSegment.orbit <= 1) {
						var segOrbit = this.orbitSegments[this.hoveredSegment.orbit]
						var segInner = segOrbit.inner * this.maxR
						var segOuter = segOrbit.outer * this.maxR
						ctx.beginPath()
						ctx.arc(0, 0, segOuter, segSector.startAngle, segSector.endAngle)
						ctx.arc(0, 0, segInner, segSector.endAngle, segSector.startAngle, true)
						ctx.closePath()
						ctx.fillStyle = 'rgba(100, 160, 255, 0.1)'
						ctx.fill()
						ctx.strokeStyle = 'rgba(100, 160, 255, 0.25)'
						ctx.lineWidth = 1 / zoom
						ctx.stroke()
					} else {
						this.drawStaleWedge(ctx, segSector.startAngle, segSector.endAngle, activeRingR, 'rgba(100, 160, 255, 0.1)', 'rgba(100, 160, 255, 0.25)', 1 / zoom)
					}

				}
			}

		},

		// Draw a stale wedge: the region between the active ring and the canvas edge
		// for a given sector angle range
		drawStaleWedge: function(ctx, startAngle, endAngle, activeR, fill, stroke, lineWidth) {

			var steps = 20
			ctx.beginPath()

			// Inner arc (active ring boundary)
			ctx.arc(0, 0, activeR, startAngle, endAngle)

			// Outer boundary: walk along canvas edges from endAngle back to startAngle
			for (var i = steps; i >= 0; i--) {
				var a = startAngle + (endAngle - startAngle) * (i / steps)
				var ep = canvasEdgePoint(a, this.halfW, this.halfH)
				ctx.lineTo(ep.x, ep.y)
			}

			ctx.closePath()
			ctx.fillStyle = fill
			ctx.fill()
			ctx.strokeStyle = stroke
			ctx.lineWidth = lineWidth
			ctx.stroke()

		},


		// Draw the two orbit ring circles and their labels (FRESH / ACTIVE)
		renderOrbits: function(ctx, zoom) {

			ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
			ctx.lineWidth = 1 / zoom

			var ringKeys = ['f', 'a']
			for (var r = 0; r < ringKeys.length; r++) {
				var ringRadius = this.orbitRing[ringKeys[r]] * this.maxR
				ctx.beginPath()
				ctx.arc(0, 0, ringRadius, 0, Math.PI * 2)
				ctx.stroke()
			}

			var fontSize = Math.max(8, 10 / zoom)
			ctx.font = fontSize + 'px -apple-system, BlinkMacSystemFont, sans-serif'
			ctx.fillStyle = 'rgba(255, 255, 255, 0.18)'
			ctx.textAlign = 'center'
			ctx.textBaseline = 'alphabetic'

			var labels = [
				{ key: 'f', text: 'FRESH' },
				{ key: 'a', text: 'ACTIVE' }
			]

			for (var l = 0; l < labels.length; l++) {
				var labelR = this.orbitRing[labels[l].key] * this.maxR
				ctx.fillText(labels[l].text, 0, -labelR + fontSize + 4 / zoom)
			}

			// STALE label — just inside the crust at the top
			var crustW = CRUST_BAND / zoom
			ctx.fillText('STALE', 0, -this.halfH + crustW + fontSize + 4 / zoom)

		},

		// Draw all game dots (skipping the hovered one, which is drawn on top later).
		// At high zoom, crossfades from coloured dots to clipped thumbnail images.
		// When connectedSet is provided, connected dots are skipped here (drawn brighter later)
		// and all other dots are dimmed.
		renderDots: function(ctx, zoom, hoveredIndex, imageFactor, imgWorldSize, viewLeft, viewRight, viewTop, viewBottom, connectedSet) {

			var alpha = (this.selectedGame !== null) ? 0.3 : 0.85
			var intro = this.introProgress

			for (var i = 0; i < this.nodes.length; i++) {

				var node = this.nodes[i]
				if (i === hoveredIndex) continue
				if (i === this.selectedGame) continue

				// Skip connected dots — they'll be drawn on top later
				if (connectedSet && connectedSet[i]) continue

				// Interpolate position during intro animation
				var nx = node.wx * intro
				var ny = node.wy * intro

				// Cull nodes outside the visible viewport
				var margin = imgWorldSize
				if (nx < viewLeft - margin || nx > viewRight + margin || ny < viewTop - margin || ny > viewBottom + margin) continue

				var drawR = node.r / zoom

				if (imageFactor > 0) {

					// Cap image size to nearest-neighbour distance so thumbnails don't overlap
					var nodeImgSize = Math.min(imgWorldSize, node.nn * 0.9)
					if (nodeImgSize < DOT_MIN * 2 / zoom) nodeImgSize = 0  // too small — skip image

					var img = nodeImgSize > 0 ? this.getImage(node.game.g) : null

					if (img && img.complete && img.naturalWidth > 0) {

						// Crossfade: dot fading out, image fading in
						if (imageFactor < 1) {
							ctx.beginPath()
							ctx.arc(nx, ny, drawR, 0, Math.PI * 2)
							ctx.fillStyle = this.colourWithAlpha(node.colour, alpha * (1 - imageFactor))
							ctx.fill()
						}

						// Circular-clipped thumbnail
						ctx.save()
						ctx.beginPath()
						ctx.arc(nx, ny, nodeImgSize / 2, 0, Math.PI * 2)
						ctx.clip()
						ctx.globalAlpha = alpha * imageFactor + (1 - imageFactor) * 0.3
						ctx.drawImage(img, nx - nodeImgSize / 2, ny - nodeImgSize / 2, nodeImgSize, nodeImgSize)
						ctx.restore()

						// Coloured ring border
						ctx.beginPath()
						ctx.arc(nx, ny, nodeImgSize / 2, 0, Math.PI * 2)
						ctx.strokeStyle = this.colourWithAlpha(node.colour, alpha * imageFactor)
						ctx.lineWidth = 1.5 / zoom
						ctx.stroke()

					} else {

						// Image not loaded yet or too dense — draw dot as fallback
						ctx.beginPath()
						ctx.arc(nx, ny, drawR, 0, Math.PI * 2)
						ctx.fillStyle = this.colourWithAlpha(node.colour, alpha)
						ctx.fill()

					}

				} else {

					// No images at this zoom level — plain dot
					ctx.beginPath()
					ctx.arc(nx, ny, drawR, 0, Math.PI * 2)
					ctx.fillStyle = this.colourWithAlpha(node.colour, alpha)
					ctx.fill()

				}

			}

		},

		// Draw the hover effect: optionally dashed line + age label,
		// and the hovered dot itself drawn on top (with animated grow)
		renderHoverEffect: function(ctx, zoom, h, imageFactor, imgWorldSize, showLine) {

			var hp = this.hoverProgress
			var lineEase = 1 - (1 - hp) * (1 - hp)

			if (showLine) {
				// Dashed line from centre to hovered dot
				ctx.globalAlpha = lineEase
				ctx.beginPath()
				ctx.moveTo(0, 0)
				ctx.lineTo(h.wx, h.wy)
				ctx.strokeStyle = 'rgba(120, 180, 255, 0.5)'
				ctx.lineWidth = 1.5 / zoom
				ctx.setLineDash([6 / zoom, 4 / zoom])
				ctx.stroke()
				ctx.setLineDash([])

				// Age label pill
				var ageText = formatAge(h.game.ld)
				if (ageText) {
					this.renderAgeLabel(ctx, zoom, h, ageText)
				}
			}

			ctx.globalAlpha = 1

			// Hovered dot — drawn on top with smooth grow animation
			var ease = 1 - (1 - hp) * (1 - hp)
			var hBaseR = h.r / zoom
			var hGrowR = hBaseR + (4 / zoom) * ease

			if (imageFactor > 0) {

				var hImg = this.getImage(h.game.g)
				var hImgSize = imgWorldSize + (6 / zoom) * ease

				if (hImg && hImg.complete && hImg.naturalWidth > 0) {

					// Circular-clipped thumbnail with white + colour double ring
					ctx.save()
					ctx.beginPath()
					ctx.arc(h.wx, h.wy, hImgSize / 2, 0, Math.PI * 2)
					ctx.clip()
					ctx.drawImage(hImg, h.wx - hImgSize / 2, h.wy - hImgSize / 2, hImgSize, hImgSize)
					ctx.restore()

					ctx.beginPath()
					ctx.arc(h.wx, h.wy, hImgSize / 2, 0, Math.PI * 2)
					ctx.strokeStyle = '#fff'
					ctx.lineWidth = 2 / zoom
					ctx.stroke()

					ctx.beginPath()
					ctx.arc(h.wx, h.wy, hImgSize / 2 + 1.5 / zoom, 0, Math.PI * 2)
					ctx.strokeStyle = h.colour
					ctx.lineWidth = 1.5 / zoom
					ctx.stroke()

				} else {

					// Fallback dot with white border
					ctx.beginPath()
					ctx.arc(h.wx, h.wy, hGrowR, 0, Math.PI * 2)
					ctx.fillStyle = h.colour
					ctx.fill()
					ctx.strokeStyle = '#fff'
					ctx.lineWidth = 2 / zoom
					ctx.stroke()

				}

			} else {

				ctx.beginPath()
				ctx.arc(h.wx, h.wy, hGrowR, 0, Math.PI * 2)
				ctx.fillStyle = h.colour
				ctx.fill()
				ctx.strokeStyle = '#fff'
				ctx.lineWidth = 2 / zoom
				ctx.stroke()

			}

		},

		// Draw the selected game dot — large thumbnail with prominent ring
		renderSelectedDot: function(ctx, zoom, node, imageFactor, imgWorldSize) {

			var img = this.getImage(node.game.g)
			var size = Math.max(imgWorldSize, 16 / zoom) * 2
			var growSize = size

			if (img && img.complete && img.naturalWidth > 0) {

				// Circular-clipped thumbnail with white + colour double ring
				ctx.save()
				ctx.beginPath()
				ctx.arc(node.wx, node.wy, growSize / 2, 0, Math.PI * 2)
				ctx.clip()
				ctx.drawImage(img, node.wx - growSize / 2, node.wy - growSize / 2, growSize, growSize)
				ctx.restore()

				// White inner ring
				ctx.beginPath()
				ctx.arc(node.wx, node.wy, growSize / 2, 0, Math.PI * 2)
				ctx.strokeStyle = '#fff'
				ctx.lineWidth = 2.5 / zoom
				ctx.stroke()

				// Colour outer ring
				ctx.beginPath()
				ctx.arc(node.wx, node.wy, growSize / 2 + 2 / zoom, 0, Math.PI * 2)
				ctx.strokeStyle = node.colour
				ctx.lineWidth = 1.5 / zoom
				ctx.stroke()

			} else {

				// Fallback: bright dot with white + colour rings
				ctx.beginPath()
				ctx.arc(node.wx, node.wy, node.r / zoom + 4 / zoom, 0, Math.PI * 2)
				ctx.fillStyle = node.colour
				ctx.fill()
				ctx.strokeStyle = '#fff'
				ctx.lineWidth = 2.5 / zoom
				ctx.stroke()

			}

			// Dashed line from centre to selected dot
			ctx.globalAlpha = 0.8
			ctx.beginPath()
			ctx.moveTo(0, 0)
			ctx.lineTo(node.wx, node.wy)
			ctx.strokeStyle = 'rgba(120, 180, 255, 0.5)'
			ctx.lineWidth = 1.5 / zoom
			ctx.setLineDash([6 / zoom, 4 / zoom])
			ctx.stroke()
			ctx.setLineDash([])
			ctx.globalAlpha = 1

			// Age label — always shown for selected game
			{
				var ageText = formatAge(node.game.ld)
				if (ageText) {
					this.renderAgeLabel(ctx, zoom, node, ageText)
				}
			}

		},

		// Draw connection lines from the hovered/selected dot to all connected dots
		renderConnections: function(ctx, zoom, hoveredIndex, connectedSet) {

			var h = this.nodes[hoveredIndex]
			var intro = this.introProgress
			var hx = h.wx * intro
			var hy = h.wy * intro

			ctx.lineWidth = 1.2 / zoom
			ctx.strokeStyle = CONNECTION_COLOUR

			for (var idx in connectedSet) {
				var target = this.nodes[idx]
				if (!target) continue

				var tx = target.wx * intro
				var ty = target.wy * intro

				ctx.beginPath()
				ctx.moveTo(hx, hy)
				ctx.lineTo(tx, ty)
				ctx.stroke()
			}

		},

		// Re-draw connected dots at full brightness on top of connection lines
		renderConnectedDots: function(ctx, zoom, hoveredIndex, connectedSet, imageFactor, imgWorldSize, viewLeft, viewRight, viewTop, viewBottom) {

			var alpha = 0.9
			var intro = this.introProgress

			for (var idx in connectedSet) {
				var ni = parseInt(idx)
				var node = this.nodes[ni]
				if (!node || ni === hoveredIndex) continue

				var nx = node.wx * intro
				var ny = node.wy * intro

				var margin = imgWorldSize
				if (nx < viewLeft - margin || nx > viewRight + margin || ny < viewTop - margin || ny > viewBottom + margin) continue

				var drawR = node.r / zoom

				if (imageFactor > 0) {

					var connImgSize = Math.min(imgWorldSize, node.nn * 0.9)
					if (connImgSize < DOT_MIN * 2 / zoom) connImgSize = 0

					var img = connImgSize > 0 ? this.getImage(node.game.g) : null

					if (img && img.complete && img.naturalWidth > 0) {

						if (imageFactor < 1) {
							ctx.beginPath()
							ctx.arc(nx, ny, drawR, 0, Math.PI * 2)
							ctx.fillStyle = this.colourWithAlpha(node.colour, alpha * (1 - imageFactor))
							ctx.fill()
						}

						ctx.save()
						ctx.beginPath()
						ctx.arc(nx, ny, connImgSize / 2, 0, Math.PI * 2)
						ctx.clip()
						ctx.globalAlpha = alpha * imageFactor + (1 - imageFactor) * 0.3
						ctx.drawImage(img, nx - connImgSize / 2, ny - connImgSize / 2, connImgSize, connImgSize)
						ctx.restore()

						ctx.beginPath()
						ctx.arc(nx, ny, connImgSize / 2, 0, Math.PI * 2)
						ctx.strokeStyle = this.colourWithAlpha(node.colour, alpha * imageFactor)
						ctx.lineWidth = 1.5 / zoom
						ctx.stroke()

					} else {

						ctx.beginPath()
						ctx.arc(nx, ny, drawR, 0, Math.PI * 2)
						ctx.fillStyle = this.colourWithAlpha(node.colour, alpha)
						ctx.fill()

					}

				} else {

					// Bright dot with subtle glow
					ctx.beginPath()
					ctx.arc(nx, ny, drawR + 1 / zoom, 0, Math.PI * 2)
					ctx.fillStyle = this.colourWithAlpha(node.colour, 0.3)
					ctx.fill()

					ctx.beginPath()
					ctx.arc(nx, ny, drawR, 0, Math.PI * 2)
					ctx.fillStyle = this.colourWithAlpha(node.colour, alpha)
					ctx.fill()

				}

			}

		},

		// Draw the age label pill on the hover line. When the universe centre is
		// visible it anchors at (0,0) extending away from the dot; when zoomed
		// so the centre is off-screen, it floats where the line enters the viewport.
		renderAgeLabel: function(ctx, zoom, h, ageText) {

			var lineAngle = Math.atan2(h.wy, h.wx)
			var perpX = -Math.sin(lineAngle) * 14 / zoom
			var perpY = Math.cos(lineAngle) * 14 / zoom

			var labelMargin = 40 / zoom
			var vLeft = this.panX - this.canvasWidth / 2 / zoom + labelMargin
			var vRight = this.panX + this.canvasWidth / 2 / zoom - labelMargin
			var vTop = this.panY - this.canvasHeight / 2 / zoom + labelMargin
			var vBottom = this.panY + this.canvasHeight / 2 / zoom - labelMargin

			var centerVisible = (0 >= vLeft && 0 <= vRight && 0 >= vTop && 0 <= vBottom)
			var labelX, labelY, atCenter

			if (centerVisible) {
				labelX = 0
				labelY = 0
				atCenter = true
			} else {
				atCenter = false
				// Walk along the line from dot toward centre to find where it enters the viewport
				var bestT = 0.5
				var edges = [
					{ axis: 'x', val: vLeft, comp: h.wx },
					{ axis: 'x', val: vRight, comp: h.wx },
					{ axis: 'y', val: vTop, comp: h.wy },
					{ axis: 'y', val: vBottom, comp: h.wy }
				]
				for (var ei = 0; ei < edges.length; ei++) {
					var e = edges[ei]
					var denom = (e.axis === 'x') ? -h.wx : -h.wy
					if (Math.abs(denom) < 0.001) continue
					var t = (e.val - ((e.axis === 'x') ? h.wx : h.wy)) / denom
					if (t > 0.05 && t < 0.95) {
						var px = h.wx + t * (-h.wx)
						var py = h.wy + t * (-h.wy)
						if (px >= vLeft - 1 && px <= vRight + 1 && py >= vTop - 1 && py <= vBottom + 1) {
							if (t < bestT) bestT = t
						}
					}
				}
				labelX = h.wx + bestT * (-h.wx) + perpX
				labelY = h.wy + bestT * (-h.wy) + perpY
			}

			// Clamp to viewport
			labelX = Math.max(vLeft, Math.min(vRight, labelX))
			labelY = Math.max(vTop, Math.min(vBottom, labelY))

			// Draw at fixed screen size (undo zoom transform for crisp text)
			ctx.save()
			ctx.translate(labelX, labelY)
			ctx.scale(1 / zoom, 1 / zoom)

			var ageFontSize = 14
			ctx.font = '600 ' + ageFontSize + 'px -apple-system, BlinkMacSystemFont, sans-serif'
			var textWidth = ctx.measureText(ageText).width
			var padH = 8
			var padV = 5
			var boxW = textWidth + padH * 2
			var boxH = ageFontSize + padV * 2
			var boxR = 6

			if (atCenter) {
				// Anchor corner at centre, extend away from the line direction
				var awayX = -Math.cos(lineAngle)
				var awayY = -Math.sin(lineAngle)
				var boxOffX = (awayX >= 0) ? 0 : -boxW
				var boxOffY = (awayY >= 0) ? 0 : -boxH

				ctx.fillStyle = '#4881bb'
				ctx.beginPath()
				ctx.roundRect(boxOffX, boxOffY, boxW, boxH, boxR)
				ctx.fill()

				ctx.fillStyle = '#fff'
				ctx.textAlign = 'center'
				ctx.textBaseline = 'middle'
				ctx.fillText(ageText, boxOffX + boxW / 2, boxOffY + boxH / 2 + 1)
			} else {
				// Centred on the line entry point
				ctx.fillStyle = '#4881bb'
				ctx.beginPath()
				ctx.roundRect(-boxW / 2, -boxH / 2, boxW, boxH, boxR)
				ctx.fill()

				ctx.fillStyle = '#fff'
				ctx.textAlign = 'center'
				ctx.textBaseline = 'middle'
				ctx.fillText(ageText, 0, 1)
			}

			ctx.restore()

		},

		// -------------------------------------------------------------------
		// Image loading — lazy-loads thumbnails with concurrency throttle
		// -------------------------------------------------------------------

		getImage: function(gameId) {

			if (this.imageCache[gameId]) return this.imageCache[gameId]

			if (this.imageLoading >= IMAGE_LOAD_LIMIT) return null

			this.imageLoading++

			var img = new Image()
			var self = this

			img.onload = function() {
				self.imageLoading--
				self.scheduleRender()
			}

			img.onerror = function() {
				self.imageLoading--
			}

			img.src = self.$util.thumbnail.game.small(gameId)

			this.imageCache[gameId] = img
			return img

		},

		// Coalesce rapid image-load completions into a single re-render
		scheduleRender: function() {

			if (this.renderTimeout) return
			this.renderTimeout = setTimeout(function() {
				this.renderTimeout = null
				this.render()
			}.bind(this), 50)

		},

		// -------------------------------------------------------------------
		// Hover animation — grows the hovered dot over 200ms
		// -------------------------------------------------------------------

		// Intro fly-out: dots expand from centre over 1.5s with ease-out
		animateIntro: function() {

			var self = this
			var start = performance.now()
			var duration = 1500

			function step(now) {
				var t = Math.min(1, (now - start) / duration)
				// Ease-out cubic
				self.introProgress = 1 - (1 - t) * (1 - t) * (1 - t)
				self.render()
				if (t < 1) {
					requestAnimationFrame(step)
				} else {
					self.introAnimating = false
				}
			}

			requestAnimationFrame(function(now) {
				start = now
				step(now)
			})

		},

		animateHover: function() {

			if (this.hovered === null) {
				this.hoverProgress = 0
				this.render()
				return
			}

			var self = this
			var start = performance.now()
			var duration = 200

			function step(now) {
				if (self.hovered === null || !self.hoverAnimating) return
				self.hoverProgress = Math.min(1, (now - start) / duration)
				self.render()
				if (self.hoverProgress < 1) {
					requestAnimationFrame(step)
				}
			}

			this.hoverAnimating = true
			requestAnimationFrame(function(now) {
				start = now
				step(now)
			})

		},

		// -------------------------------------------------------------------
		// Utility
		// -------------------------------------------------------------------

		// Convert "#rrggbb" hex to "rgba(r,g,b,a)"
		colourWithAlpha: function(hex, alpha) {

			var r = parseInt(hex.slice(1, 3), 16)
			var g = parseInt(hex.slice(3, 5), 16)
			var b = parseInt(hex.slice(5, 7), 16)
			return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')'

		},

		// -------------------------------------------------------------------
		// Input handlers — zoom, pan, hover, click
		// -------------------------------------------------------------------

		// Mouse wheel: zoom toward cursor position
		onWheel: function(e) {

			var delta = e.deltaY > 0 ? 0.9 : 1.1
			var newZoom = Math.max(1, Math.min(this.maxZoom, this.zoom * delta))

			// Already at min zoom and scrolling out — forward to page scroll
			if (newZoom === this.zoom && e.deltaY > 0) {
				window.scrollBy(0, e.deltaY)
				return
			}

			var rect = this.$refs.canvas.getBoundingClientRect()
			var mx = e.clientX - rect.left
			var my = e.clientY - rect.top

			// World position under the cursor before zoom change
			var world = this.screenToWorld(mx, my)

			// Adjust pan so the world point under the cursor stays fixed
			this.panX = world.x - (mx - this.canvasWidth / 2) / newZoom
			this.panY = world.y - (my - this.canvasHeight / 2) / newZoom
			this.zoom = newZoom

			if (newZoom <= 1) {
				this.panX = 0
				this.panY = 0
			} else {
				this.clampPan()
			}

			this.hovered = null
			this.hoveredSegment = null
			this.hoveredSector = null

			this.tooltip = null

			// Update selected tooltip position during zoom
			if (this.selectedGame !== null && this.selectedTooltip) {
				var selNode = this.nodes[this.selectedGame]
				if (selNode) {
					var selScreen = this.worldToScreen(selNode.wx, selNode.wy)
					this.selectedTooltipX = selScreen.x
					this.selectedTooltipY = selScreen.y
					this.updateSelectedTooltipPosition()
				}
			}

			this.render()

			// Begin preloading thumbnails once we're zoomed enough to show them
			if (newZoom >= this.imageZoomStart) {
				this.preloadVisibleImages()
			}

			// Re-detect what's under the cursor at the new zoom level
			this.onMouseMove(e)

		},

		// Preload thumbnails for all nodes currently inside the viewport
		preloadVisibleImages: function() {

			var viewLeft = this.panX - this.canvasWidth / 2 / this.zoom
			var viewRight = this.panX + this.canvasWidth / 2 / this.zoom
			var viewTop = this.panY - this.canvasHeight / 2 / this.zoom
			var viewBottom = this.panY + this.canvasHeight / 2 / this.zoom

			for (var i = 0; i < this.nodes.length; i++) {
				var n = this.nodes[i]
				if (n.wx >= viewLeft && n.wx <= viewRight && n.wy >= viewTop && n.wy <= viewBottom) {
					this.getImage(n.game.g)
				}
			}

		},

		// Mouse down: begin drag-to-pan (only when zoomed in)
		onMouseDown: function(e) {

			if (e.button !== 0 || this.zoom <= 1) return

			this.isPanning = true
			this.hasPanned = false
			this.panStartX = e.clientX
			this.panStartY = e.clientY
			this.panStartPanX = this.panX
			this.panStartPanY = this.panY

			document.addEventListener('mousemove', this.mouseMoveHandler)
			document.addEventListener('mouseup', this.mouseUpHandler)

		},

		// Global mouse move during pan drag
		onGlobalMouseMove: function(e) {

			if (!this.isPanning) return

			var dx = e.clientX - this.panStartX
			var dy = e.clientY - this.panStartY

			// Mark as a drag (not a click) once moved past 3px threshold
			if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
				this.hasPanned = true
			}

			this.panX = this.panStartPanX - dx / this.zoom
			this.panY = this.panStartPanY - dy / this.zoom

			this.clampPan()

			this.hovered = null
			this.tooltip = null

			// Update selected tooltip position during pan
			if (this.selectedGame !== null && this.selectedTooltip) {
				var selNode = this.nodes[this.selectedGame]
				if (selNode) {
					var selScreen = this.worldToScreen(selNode.wx, selNode.wy)
					this.selectedTooltipX = selScreen.x
					this.selectedTooltipY = selScreen.y
					this.updateSelectedTooltipPosition()
				}
			}

			this.render()

		},

		// Global mouse up: end pan drag
		onGlobalMouseUp: function() {

			this.isPanning = false
			document.removeEventListener('mousemove', this.mouseMoveHandler)
			document.removeEventListener('mouseup', this.mouseUpHandler)

		},

		// Mouse move over canvas: detect hovered dot, segment, and crust sector
		onMouseMove: function(e) {

			if (this.isPanning || this.$isMobile) return

			var rect = this.$refs.canvas.getBoundingClientRect()
			var mx = e.clientX - rect.left
			var my = e.clientY - rect.top
			var world = this.screenToWorld(mx, my)

			// --- Segment and crust sector hit-testing ---

			var distFromCenter = Math.sqrt(world.x * world.x + world.y * world.y)
			var activeRingR = this.orbitRing.a * this.maxR
			var crustBand = CRUST_BAND / this.zoom
			var newHovSector = null
			var newHovSegment = null

			// Normalise angle to match sector range (-π/2 to 3π/2)
			var mouseAngle = Math.atan2(world.y, world.x)
			if (mouseAngle < -Math.PI / 2) mouseAngle += Math.PI * 2

			// Find which sector the cursor angle falls in (handles wrapping sector)
			var sectorIndex = null
			for (var si = 0; si < this.sectors.length; si++) {
				var sec = this.sectors[si]
				if (sec.count === 0) continue
				if (sec.endAngle > Math.PI * 3 / 2) {
					// Wrapping sector: matches if angle >= start OR angle <= end-2π
					if (mouseAngle >= sec.startAngle || mouseAngle <= sec.endAngle - Math.PI * 2) {
						sectorIndex = si
						break
					}
				} else if (mouseAngle >= sec.startAngle && mouseAngle <= sec.endAngle) {
					sectorIndex = si
					break
				}
			}

			// Check if cursor is in a fixed-position crust band (by perimeter position)
			var inCrust = (
				world.x < -this.halfW + crustBand ||
				world.x > this.halfW - crustBand ||
				world.y < -this.halfH + crustBand ||
				world.y > this.halfH - crustBand
			) && (
				world.x >= -this.halfW && world.x <= this.halfW &&
				world.y >= -this.halfH && world.y <= this.halfH
			)

			var crustSectorIndex = null
			if (inCrust && this.crustRanges.length) {
				var perim = 4 * this.halfW + 4 * this.halfH
				var mousePerim = worldToPerimeterPos(world.x, world.y, this.halfW, this.halfH)
				for (var ci = 0; ci < this.crustRanges.length; ci++) {
					var cr = this.crustRanges[ci]
					if (!cr) continue
					var crEnd = cr.start + cr.length
					if (crEnd <= perim) {
						if (mousePerim >= cr.start && mousePerim <= crEnd) { crustSectorIndex = ci; break }
					} else {
						// Band wraps around perimeter origin
						if (mousePerim >= cr.start || mousePerim <= crEnd - perim) { crustSectorIndex = ci; break }
					}
				}
			}

			if (crustSectorIndex !== null) {

				// Over a crust band — highlight that sector
				newHovSector = crustSectorIndex

			} else if (sectorIndex !== null) {

				if (distFromCenter <= activeRingR) {
					// Inside the circular core — highlight the orbit segment
					newHovSector = sectorIndex
					for (var oi = 0; oi < this.orbitSegments.length; oi++) {
						var orb = this.orbitSegments[oi]
						if (distFromCenter >= orb.inner * this.maxR && distFromCenter <= orb.outer * this.maxR) {
							newHovSegment = { orbit: oi, sector: sectorIndex }
							break
						}
					}
				} else if (distFromCenter > activeRingR && !inCrust) {
					// In the stale zone (between active ring and canvas edge, outside crust)
					newHovSector = sectorIndex
					newHovSegment = { orbit: 2, sector: sectorIndex }
				}

			}

			// Check if segment hover changed
			var segChanged = false
			if (newHovSegment === null && this.hoveredSegment !== null) segChanged = true
			else if (newHovSegment !== null && this.hoveredSegment === null) segChanged = true
			else if (newHovSegment !== null && this.hoveredSegment !== null && (newHovSegment.orbit !== this.hoveredSegment.orbit || newHovSegment.sector !== this.hoveredSegment.sector)) segChanged = true

			if (newHovSector !== this.hoveredSector || segChanged) {
				this.hoveredSector = newHovSector
				this.hoveredSegment = newHovSegment
				this.render()
			}

			// --- Nearest dot hit-testing ---

			var closest = null
			var closestDist = 12 / this.zoom

			for (var i = 0; i < this.nodes.length; i++) {
				var node = this.nodes[i]
				var ndx = node.wx - world.x
				var ndy = node.wy - world.y
				var dist = Math.sqrt(ndx * ndx + ndy * ndy)
				if (dist < closestDist) {
					closest = i
					closestDist = dist
				}
			}

			if (closest !== this.hovered) {
				this.hoverAnimating = false
				this.hovered = closest
				this.hoverProgress = 0
				this.animateHover()
			}

			// --- Hover tooltip ---

			if (closest !== null && closest !== this.selectedGame) {
				this.buildDotTooltip(closest)
			} else if (closest === null && newHovSegment !== null && this.selectedGame === null) {
				this.buildSegmentTooltip(newHovSegment, world)
			} else if (closest === null || closest === this.selectedGame) {
				this.tooltip = null
			}

			// --- Update selected tooltip position (tracks during pan/zoom) ---
			if (this.selectedGame !== null && this.selectedTooltip) {
				var selNode = this.nodes[this.selectedGame]
				if (selNode) {
					var selScreen = this.worldToScreen(selNode.wx, selNode.wy)
					this.selectedTooltipX = selScreen.x
					this.selectedTooltipY = selScreen.y
					this.updateSelectedTooltipPosition()
				}
			}

			// Pointer cursor when hovering anything interactive
			this.$refs.canvas.style.cursor = (closest !== null || newHovSegment !== null || newHovSector !== null) ? 'pointer' : 'default'

		},

		onMouseLeave: function() {

			if ((this.hovered !== null || this.hoveredSector !== null || this.hoveredSegment !== null) && !this.isPanning) {
				this.hovered = null
				this.hoverAnimating = false
				this.hoverProgress = 0
				this.hoveredSector = null
				this.hoveredSegment = null
				this.tooltip = null
				this.render()
			}

		},

		// -------------------------------------------------------------------
		// Tooltips — build tooltip data for dots and segments
		// -------------------------------------------------------------------

		// Build hover tooltip for a game dot
		buildDotTooltip: function(index) {

			var n = this.nodes[index]
			var game = this.games[n.game.g] || {}

			var lastLabels = { played: 'Played', rated: 'Rated', owned: 'Own', prevowned: 'Prev. Owned', wishlisted: 'Wishlisted', collected: 'Collected' }
			var lastLabel = n.game.lt ? (lastLabels[n.game.lt] || 'Last') : (n.game.ld ? 'Last' : null)

			// Check if this game is connected to the selected game
			var connectedLabel = null
			if (this.selectedGame !== null && this.selectedGame !== index) {
				var selNode = this.nodes[this.selectedGame]
				if (selNode) {
					var selConn = this.connectionIndex[selNode.game.g]
					if (selConn && selConn[index]) {
						var connTypeLabels = { d: 'designer', m: 'mechanic', p: 'publisher', c: 'category' }
						connectedLabel = 'Connected by ' + (connTypeLabels[this.linkType] || 'connection')
					}
				}
			}

			var screen = this.worldToScreen(n.wx, n.wy)

			this.tooltip = {
				name: game.name || ('Game #' + n.game.g),
				lastDate: formatDate(n.game.ld),
				lastLabel: lastLabel,
				connectedLabel: connectedLabel,
				isSegment: false
			}
			this.tooltipX = screen.x
			this.tooltipY = screen.y
			this.positionTooltip()

		},

		// Build selected tooltip for a game dot (full details)
		buildSelectedTooltip: function(index) {

			var n = this.nodes[index]
			var game = this.games[n.game.g] || {}

			var ftLabel = n.game.ft ? n.game.ft.charAt(0).toUpperCase() + n.game.ft.slice(1) : null

			var screen = this.worldToScreen(n.wx, n.wy)

			var connEntry = this.connectionIndex[n.game.g]
			var connCount = connEntry ? Object.keys(connEntry).length : 0
			var connLabels = { d: 'Designers', m: 'Mechanics', p: 'Publishers', c: 'Categories' }
			var connType = connLabels[this.linkType] || 'Connections'

			var isOwned = (n.game.f & 1) !== 0
			var isPrevowned = (n.game.f & 2) !== 0

			this.selectedTooltip = {
				name: game.name || ('Game #' + n.game.g),
				year: game.year || null,
				firstType: ftLabel,
				rating: n.game.r > 0 ? n.game.r : null,
				plays: n.game.p || 0,
				ownedDateRaw: n.game.od || null,
				ownLabel: isOwned ? 'Own' : (isPrevowned ? 'Prev. Owned' : 'Own'),
				firstPlayedRaw: n.game.fp || null,
				lastPlayedRaw: n.game.lp || null,
				connections: connCount,
				connectionType: connType,
				gameId: n.game.g
			}
			this.selectedTooltipX = screen.x
			this.selectedTooltipY = screen.y
			this.positionSelectedTooltip()

		},

		// Build tooltip for a segment (orbit × sector intersection)
		buildSegmentTooltip: function(segment, world) {

			var segSector = this.sectors[segment.sector]
			var orbitSubs = ['Last 6 months', '6 months – 2 years', 'Over 2 years']
			var orbitKey = (segment.orbit <= 1) ? this.orbitSegments[segment.orbit].key : 's'

			// Count games in this specific segment
			var segCount = 0
			var segGames = this.data.games
			for (var gi = 0; gi < segGames.length; gi++) {
				if (segGames[gi].o === orbitKey && ratingBand(segGames[gi].r) === segment.sector) {
					segCount++
				}
			}

			var segScreen = this.worldToScreen(world.x, world.y)

			this.tooltip = {
				segOrbit: orbitSubs[segment.orbit],
				segRating: segSector.label === 'Unrated' ? 'Unrated' : 'Rated ' + segSector.label,
				segCount: segCount,
				isSegment: true
			}
			this.tooltipX = segScreen.x
			this.tooltipY = segScreen.y
			this.positionTooltip()

		},

		// Position the hover / segment tooltip
		positionTooltip: function() {

			var isSegment = this.tooltip && this.tooltip.isSegment
			var selector = isSegment ? '.universe-segment-tooltip' : '.universe-tooltip:not(.is-selected)'
			this.tooltipStyle = this.computeTooltipPosition(this.tooltipX, this.tooltipY, 14, selector)
			this.tooltipCorner = this._lastCorner

		},

		// Position the selected tooltip — defers to nextTick on first show
		positionSelectedTooltip: function() {

			this.selectedTooltipStyle = { left: '-9999px', top: '-9999px' }
			var self = this
			this.$nextTick(function() {
				self.selectedTooltipStyle = self.computeTooltipPosition(self.selectedTooltipX, self.selectedTooltipY, 20, '.universe-tooltip.is-selected')
				self.selectedTooltipCorner = self._lastCorner
			})

		},

		// Update selected tooltip position (no nextTick needed — already rendered)
		updateSelectedTooltipPosition: function() {

			this.selectedTooltipStyle = this.computeTooltipPosition(this.selectedTooltipX, this.selectedTooltipY, 20, '.universe-tooltip.is-selected')
			this.selectedTooltipCorner = this._lastCorner

		},

		// Compute tooltip left/top — always a corner aligned with the game dot,
		// never overlapping it. Tries preferred quadrant (outward from centre)
		// first, then falls back to other corners that fit within bounds.
		computeTooltipPosition: function(sx, sy, gap, selector) {

			var origin = this.worldToScreen(0, 0)
			var dx = sx - origin.x
			var dy = sy - origin.y
			var dist = Math.sqrt(dx * dx + dy * dy) || 1
			var nx = dx / dist
			var ny = dy / dist

			// Measure actual tooltip size
			var tipEl = this.$el ? this.$el.querySelector(selector) : null
			var tipW = tipEl ? tipEl.offsetWidth : 240
			var tipH = tipEl ? tipEl.offsetHeight : 80

			// Viewport bounds
			var rect = this.$refs.canvas ? this.$refs.canvas.getBoundingClientRect() : null
			var pad = 8
			var minX, maxX, minY, maxY

			if (this.isFullscreen) {
				minX = pad
				maxX = window.innerWidth - tipW - pad
				minY = pad
				maxY = window.innerHeight - tipH - pad
			} else if (rect) {
				minX = pad - rect.left
				maxX = window.innerWidth - rect.left - tipW - pad
				minY = -Infinity
				maxY = Infinity
			} else {
				minX = -Infinity
				maxX = Infinity
				minY = -Infinity
				maxY = Infinity
			}

			// Four candidate positions — tooltip corner aligned with the dot
			var candidates = [
				{ x: sx + gap, y: sy + gap, corner: 'tl' },
				{ x: sx - gap - tipW, y: sy + gap, corner: 'tr' },
				{ x: sx + gap, y: sy - gap - tipH, corner: 'bl' },
				{ x: sx - gap - tipW, y: sy - gap - tipH, corner: 'br' }
			]

			// Preferred order: outward from centre first
			var prefX = (nx >= 0) ? 0 : 1
			var prefY = (ny >= 0) ? 0 : 1
			var order = [
				prefY * 2 + prefX,
				prefY * 2 + (1 - prefX),
				(1 - prefY) * 2 + prefX,
				(1 - prefY) * 2 + (1 - prefX)
			]

			for (var i = 0; i < order.length; i++) {
				var c = candidates[order[i]]
				if (c.x >= minX && c.x <= maxX && c.y >= minY && c.y <= maxY) {
					this._lastCorner = c.corner
					return { left: c.x + 'px', top: c.y + 'px' }
				}
			}

			var best = candidates[order[0]]
			this._lastCorner = best.corner
			return {
				left: Math.max(minX, Math.min(maxX, best.x)) + 'px',
				top: Math.max(minY, Math.min(maxY, best.y)) + 'px'
			}

		},

		// -------------------------------------------------------------------
		// Click — navigate to game, or select segment/sector for filtering
		// -------------------------------------------------------------------

		onClick: function() {

			// Touch handled separately on mobile
			if (this.$isMobile) return

			// Ignore click at the end of a drag
			if (this.hasPanned) return

			if (this.hovered !== null && this.nodes[this.hovered]) {

				// Click on a game dot → select it (persist connections + tooltip)
				if (this.selectedGame === this.hovered) {
					// Clicking the already-selected game deselects it
					this.deselectGame()
				} else {
					this.selectedGame = this.hovered
					this.tooltip = null
					this.buildSelectedTooltip(this.hovered)
					this.render()
				}

			} else if (this.selectedGame !== null) {

				// Any non-game click while selected → deselect
				this.deselectGame()

			} else if (this.hoveredSegment !== null) {

				// Click on an orbit × sector segment → open panel directly
				var seg = this.hoveredSegment
				var orbitKeys = ['f', 'a', 's']
				var orbitNames = ['Fresh', 'Active', 'Stale']
				var segSector = this.sectors[seg.sector]
				this.$pubsub.$emit('panel.open', { type: 'insightsGamesUniverse', title: orbitNames[seg.orbit] + ' · ' + segSector.label, segment: { orbit: orbitKeys[seg.orbit], sector: seg.sector } })

			} else if (this.hoveredSector !== null && this.hoveredSegment === null) {

				// Click on the crust (sector only, all orbits) → open panel directly
				var crustSector = this.sectors[this.hoveredSector]
				this.$pubsub.$emit('panel.open', { type: 'insightsGamesUniverse', title: crustSector.label, segment: { orbit: null, sector: this.hoveredSector } })

			}

		},

		// -------------------------------------------------------------------
		// Zoom-to-fit — animate zoom/pan to frame a selected region
		// -------------------------------------------------------------------


		// Smoothly animate zoom and pan from current values to target (ease in-out, 400ms)
		animateZoomTo: function(targetPanX, targetPanY, targetZoom) {

			// Pre-clamp target pan values
			if (targetZoom <= 1) {
				targetZoom = 1
				targetPanX = 0
				targetPanY = 0
			} else {
				var padding = PAN_PADDING / targetZoom
				var halfViewW = this.canvasWidth / 2 / targetZoom
				var halfViewH = this.canvasHeight / 2 / targetZoom
				var maxPanX = Math.max(0, this.halfW + padding - halfViewW)
				var maxPanY = Math.max(0, this.halfH + padding - halfViewH)
				targetPanX = Math.max(-maxPanX, Math.min(maxPanX, targetPanX))
				targetPanY = Math.max(-maxPanY, Math.min(maxPanY, targetPanY))
			}

			var self = this
			var startZoom = this.zoom
			var startPanX = this.panX
			var startPanY = this.panY
			var duration = 400

			// Clear hover state during animation
			this.tooltip = null
			this.hovered = null
			this.hoveredSegment = null
			this.hoveredSector = null
			this.hoverAnimating = false

			var startTime = null

			function step(now) {
				if (!startTime) startTime = now
				var t = Math.min(1, (now - startTime) / duration)

				// Ease in-out (quadratic)
				var ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

				self.zoom = startZoom + (targetZoom - startZoom) * ease
				self.panX = startPanX + (targetPanX - startPanX) * ease
				self.panY = startPanY + (targetPanY - startPanY) * ease

				if (self.zoom <= 1) {
					self.panX = 0
					self.panY = 0
				} else {
					self.clampPan()
				}

				self.render()

				if (t < 1) {
					requestAnimationFrame(step)
				} else {
					if (self.zoom >= self.imageZoomStart) {
						self.preloadVisibleImages()
					}
				}
			}

			requestAnimationFrame(step)

		},

		// -------------------------------------------------------------------
		// -------------------------------------------------------------------
		// Reset — clears selection and zooms back to 1x
		// -------------------------------------------------------------------

		resetZoom: function() {

						this.selectedGame = null
			this.tooltip = null
			this.selectedTooltip = null
			this.animateZoomTo(0, 0, 1)

		},

		// Header button: re-center and zoom out to 1x
		resetView: function() {

			this.resetZoom()

		},

		// Header button: toggle CSS-based fullscreen (stays in normal DOM so panels overlay naturally)
		toggleFullscreen: function() {

			this.isFullscreen = !this.isFullscreen
			document.body.classList.toggle('universe-fullscreen-active', this.isFullscreen)
			this.stars = []  // regenerate star field for new dimensions

			// Portal needs two ticks: one to move the DOM, another for layout to measure
			var self = this
			this.$nextTick(function() {
				self.$nextTick(function() {
					self.layout()
				})
			})

		},

		// Escape key exits CSS fullscreen
		onKeyDown: function(e) {

			if (e.key === 'Escape' && this.isFullscreen) {
				this.isFullscreen = false
				document.body.classList.remove('universe-fullscreen-active')
				this.stars = []
				var self = this
				this.$nextTick(function() {
					self.$nextTick(function() {
						self.layout()
					})
				})
			}

		},

		// -------------------------------------------------------------------
		// Touch handlers — mobile: tap to fullscreen, pinch zoom, pan, tap to select
		// -------------------------------------------------------------------

		onTouchStart: function(e) {

			if (e.touches.length === 2) {
				// Pinch start
				var dx = e.touches[0].clientX - e.touches[1].clientX
				var dy = e.touches[0].clientY - e.touches[1].clientY
				this.pinchStartDist = Math.sqrt(dx * dx + dy * dy)
				this.pinchStartZoom = this.zoom
				this.touchMoved = true
				return
			}

			if (e.touches.length === 1) {
				this.touchStartTime = Date.now()
				this.touchStartX = e.touches[0].clientX
				this.touchStartY = e.touches[0].clientY
				this.touchMoved = false
				this.panStartX = e.touches[0].clientX
				this.panStartY = e.touches[0].clientY
				this.panStartPanX = this.panX
				this.panStartPanY = this.panY
			}

		},

		onTouchMove: function(e) {

			if (e.touches.length === 2 && this.isFullscreen) {
				// Pinch zoom
				var dx = e.touches[0].clientX - e.touches[1].clientX
				var dy = e.touches[0].clientY - e.touches[1].clientY
				var dist = Math.sqrt(dx * dx + dy * dy)
				var scale = dist / this.pinchStartDist
				var newZoom = Math.max(1, Math.min(this.maxZoom, this.pinchStartZoom * scale))
				this.zoom = newZoom
				if (newZoom <= 1) { this.panX = 0; this.panY = 0 }
				else { this.clampPan() }
				this.render()
				this.touchMoved = true
				return
			}

			if (e.touches.length === 1 && this.isFullscreen && this.zoom > 1) {
				// Pan (only in fullscreen when zoomed)
				var tdx = e.touches[0].clientX - this.panStartX
				var tdy = e.touches[0].clientY - this.panStartY
				if (Math.abs(tdx) > 3 || Math.abs(tdy) > 3) this.touchMoved = true
				this.panX = this.panStartPanX - tdx / this.zoom
				this.panY = this.panStartPanY - tdy / this.zoom
				this.clampPan()
				this.tooltip = null
				this.render()
			}

		},

		onTouchEnd: function(e) {

			if (this.touchMoved || e.touches.length > 0) return

			var rect = this.$refs.canvas.getBoundingClientRect()
			var tx = this.touchStartX - rect.left
			var ty = this.touchStartY - rect.top
			var world = this.screenToWorld(tx, ty)

			if (!this.isFullscreen) {
				// First tap on non-fullscreen → go fullscreen
				this.toggleFullscreen()
				return
			}

			// In fullscreen: find nearest game dot
			var closest = null
			var closestDist = 20 / this.zoom

			for (var i = 0; i < this.nodes.length; i++) {
				var node = this.nodes[i]
				var ndx = node.wx - world.x
				var ndy = node.wy - world.y
				var dist = Math.sqrt(ndx * ndx + ndy * ndy)
				if (dist < closestDist) {
					closest = i
					closestDist = dist
				}
			}

			if (closest !== null && this.nodes[closest]) {
				if (this.selectedGame === closest) {
					this.deselectGame()
				} else {
					this.selectedGame = closest
					this.tooltip = null
					this.buildSelectedTooltip(closest)
					this.focusOnSelectedGame(closest)
				}
			} else if (this.selectedGame !== null) {
				this.deselectGame()
			} else {
				// Tap on segment → check for segment hit and open panel
				var distFromCenter = Math.sqrt(world.x * world.x + world.y * world.y)
				var activeRingR = this.orbitRing.a * this.maxR
				var mouseAngle = Math.atan2(world.y, world.x)
				if (mouseAngle < -Math.PI / 2) mouseAngle += Math.PI * 2

				var sectorIndex = null
				for (var si = 0; si < this.sectors.length; si++) {
					var sec = this.sectors[si]
					if (sec.count === 0) continue
					if (sec.endAngle > Math.PI * 3 / 2) {
						if (mouseAngle >= sec.startAngle || mouseAngle <= sec.endAngle - Math.PI * 2) { sectorIndex = si; break }
					} else if (mouseAngle >= sec.startAngle && mouseAngle <= sec.endAngle) { sectorIndex = si; break }
				}

				if (sectorIndex !== null) {
					var orbitIndex = 2 // stale
					if (distFromCenter <= activeRingR) {
						for (var oi = 0; oi < this.orbitSegments.length; oi++) {
							if (distFromCenter >= this.orbitSegments[oi].inner * this.maxR && distFromCenter <= this.orbitSegments[oi].outer * this.maxR) {
								orbitIndex = oi; break
							}
						}
					}
					var orbitKeys = ['f', 'a', 's']
					var orbitNames = ['Fresh', 'Active', 'Stale']
					var segSector = this.sectors[sectorIndex]
					this.$pubsub.$emit('panel.open', { type: 'insightsGamesUniverse', title: orbitNames[orbitIndex] + ' · ' + segSector.label, segment: { orbit: orbitKeys[orbitIndex], sector: sectorIndex } })
				}
			}

		},

		// On mobile, pan/zoom to center the selected game in the visible area above the panel
		focusOnSelectedGame: function(nodeIndex) {

			var node = this.nodes[nodeIndex]

			if (!node) { this.render(); return }

			// Estimate panel height (~180px) and offset the target upward so the game
			// sits in the center of the remaining visible canvas, not behind the panel
			var panelHeight = 180
			var offsetY = (panelHeight / 2) / this.zoom

			// Gentle zoom: bring closer if far out, but don't over-zoom
			var targetZoom = Math.max(this.zoom, Math.min(2.5, 1.8))

			this.animateZoomTo(node.wx, node.wy + offsetY, targetZoom)

		},

		// Deselect the currently selected game
		deselectGame: function() {

			this.selectedGame = null
			this.tooltip = null
			this.selectedTooltip = null
			this.render()

		},

		// Navigate to the selected game's insights page
		goToGame: function() {

			if (this.selectedGame !== null && this.nodes[this.selectedGame]) {
				var gid = this.nodes[this.selectedGame].game.g
				this.selectedGame = null
				this.tooltip = null
				this.selectedTooltip = null
				this.$router.push({ name: 'InsightsGame', params: { game: gid } })
			}

		},

		// Connection type dropdown changed — commit to Vuex (triggers dynamic refetch)
		onLinkTypeChange: function(value) {

			this.$store.commit('filter/insights/games/universe.links', value)

		},

		// Debounced resize handler — re-layouts after 150ms of no further resizes
		onResize: function() {

			clearTimeout(this.resizeTimeout)
			this.resizeTimeout = setTimeout(this.layout.bind(this), 150)

		}

	}

}

</script>

<style scoped>

/* Allow tooltips to overflow the content area */
.universe-item >>> .insights-item-content {
	overflow: visible;
}

/* ---------------------------------------------------------------------------
   Layout — full-width canvas with selection list below
   --------------------------------------------------------------------------- */

.universe {
	width: 100%;
	display: flex;
	flex-direction: column;
}

/* ---------------------------------------------------------------------------
   Canvas
   --------------------------------------------------------------------------- */

.universe-canvas-wrapper {
	position: relative;
	width: 100%;
}

.universe-canvas-wrapper canvas {
	overflow: hidden;
	display: block;
	cursor: default;
	border-radius: 6px;
	background: #0f1729;
}

.universe-canvas-wrapper canvas:active {
	cursor: grabbing;
}

/* ---------------------------------------------------------------------------
   Fullscreen
   --------------------------------------------------------------------------- */

.universe-tap-hint {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: flex-end;
	justify-content: center;
	padding-bottom: 16px;
	z-index: 10;
	cursor: pointer;
}

.universe-tap-hint:after {
	content: 'Tap to explore';
	background: #fff;
	color: #333;
	font-size: 12px;
	font-weight: 600;
	padding: 8px 18px;
	border-radius: 20px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.universe-canvas-wrapper.is-fullscreen {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 999999999;
	background: #0f1729;
}

.universe-canvas-wrapper.is-fullscreen canvas {
	border-radius: 0;
}

.universe-overlay {
	position: absolute;
	top: 36px;
	right: 36px;
	display: flex;
	align-items: center;
	gap: 12px;
	z-index: 100;
}

.is-mobile .universe-overlay {
	top: 10px;
	right: 10px;
}

.is-mobile .universe-overlay-button {
	background: #fff;
	color: #333;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.is-mobile .universe-overlay-button:hover {
	background: #f0f0f0;
	color: #111;
}

.universe-overlay-legend {
	display: flex;
	gap: 10px;
	margin-right: 6px;
}

.universe-overlay .universe-legend-item {
	color: rgba(255, 255, 255, 0.5);
}

.universe-overlay-buttons {
	display: flex;
	gap: 6px;
}

.universe-overlay-button {
	width: 36px;
	height: 36px;
	border: 0;
	border-radius: 6px;
	background: rgba(255, 255, 255, 0.1);
	color: rgba(255, 255, 255, 0.6);
	font-size: 16px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s, color 0.15s;
}

.universe-overlay-button:hover {
	background: rgba(255, 255, 255, 0.2);
	color: #fff;
}

/* ---------------------------------------------------------------------------
   Tooltips (both game and segment)
   --------------------------------------------------------------------------- */

.universe-tooltip {
	position: absolute;
	background: #fff;
	border-radius: 8px;
	padding: 10px 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.12);
	pointer-events: none;
	z-index: 100;
	max-width: 240px;
	min-width: 170px;
}

.universe-tooltip::after {
	content: '';
	position: absolute;
	width: 20px;
	height: 20px;
	background: #fff;
	z-index: -1;
}

.universe-tooltip[data-corner="tl"]::after {
	top: -4px;
	left: -4px;
	clip-path: polygon(0% 0%, 100% 30%, 30% 100%);
}

.universe-tooltip[data-corner="tr"]::after {
	top: -4px;
	right: -4px;
	clip-path: polygon(100% 0%, 0% 30%, 70% 100%);
}

.universe-tooltip[data-corner="bl"]::after {
	bottom: -4px;
	left: -4px;
	clip-path: polygon(0% 100%, 100% 70%, 30% 0%);
}

.universe-tooltip[data-corner="br"]::after {
	bottom: -4px;
	right: -4px;
	clip-path: polygon(100% 100%, 0% 70%, 70% 0%);
}

.universe-tooltip.is-selected {
	pointer-events: auto;
	max-width: 340px;
	min-width: 300px;
	padding: 12px 14px;
}

.universe-tooltip-close {
	position: absolute;
	top: 6px;
	right: 10px;
	width: 28px;
	height: 28px;
	border: 0;
	border-radius: 50%;
	background: rgba(var(--theme-color), 0.08);
	color: rgba(var(--theme-color), 0.4);
	font-size: 13px;
	cursor: pointer;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	transition: background 0.15s, color 0.15s;
}

.universe-tooltip-close:hover {
	background: rgba(var(--theme-color), 0.12);
	color: rgba(var(--theme-color), 0.7);
}

.universe-tooltip-head {
	margin-bottom: 10px;
	padding-right: 32px;
}

.universe-tooltip-name {
	font-size: 15px;
	font-weight: 600;
	color: rgba(var(--theme-color), 0.85);
	line-height: 1.25;
}

.universe-tooltip-year {
	font-size: 13px;
	color: rgba(var(--theme-color), 0.35);
	margin-top: 2px;
}

.universe-tooltip-metrics {
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	gap: 8px 4px;
	margin-bottom: 8px;
}

.universe-tooltip-metrics >>> .overview-item {
	min-height: 56px;
	grid-column: span 1;
}

.universe-tooltip-metrics >>> .overview-item-value-item {
	font-size: 20px;
	line-height: 32px;
}

.universe-tooltip-metrics >>> .overview-item-value-rating {
	width: 36px;
	font-size: 20px !important;
	height: 44px;
}

.universe-tooltip-metrics >>> .overview-item-label small {
	margin-top: 0px;
}

.universe-tooltip-metrics >>> .overview-background div {
	border-radius: 4px;
}

.universe-tooltip-metrics >>> .rating {
	background-color: transparent !important;
}

.universe-tooltip-metrics >>> .date[data-size="large"] .date-valid {
	width: 40px;
}

.universe-tooltip-metrics >>> .date[data-size="large"] .date-day,
.universe-tooltip-metrics >>> .date[data-size="large"] .date-month {
	font-size: 13px;
	line-height: 13px;
}

.universe-tooltip-metrics >>> .date[data-size="large"] .date-year {
	font-size: 16px;
	line-height: 18px;
}

.universe-tooltip-meta {
	font-size: 13px;
	color: rgba(var(--theme-color), 0.4);
	margin-bottom: 2px;
	display: flex;
	align-items: center;
	gap: 6px;
}

.universe-tooltip-count {
	font-size: 14px;
	font-weight: 600;
	color: rgba(var(--theme-color), 0.7);
	margin-top: 4px;
}

.universe-tooltip-connected {
	font-size: 12px;
	font-weight: 500;
	color: rgb(210, 165, 50);
	margin-top: 4px;
}

.universe-tooltip-hint {
	font-size: 11px;
	color: rgba(var(--theme-color), 0.25);
	margin-top: 4px;
}

.universe-tooltip-button {
	margin-top: 10px;
}

/* ---------------------------------------------------------------------------
   Mobile slide-up panel
   --------------------------------------------------------------------------- */

.universe-mobile-panel {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	background: #fff;
	padding: 10px;
	z-index: 1000000000;
	box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.2);
	pointer-events: auto;
	max-width: none;
	min-width: 0;
}

.universe-mobile-panel .universe-tooltip-head {
	padding-right: 32px;
}

.universe-panel-enter-active {
	transition: transform 200ms ease-out;
}

.universe-panel-leave-active {
	transition: transform 150ms ease-in;
}

.universe-panel-enter {
	transform: translateY(100%);
}

.universe-panel-leave-to {
	transform: translateY(100%);
}

/* ---------------------------------------------------------------------------
   Legend
   --------------------------------------------------------------------------- */

.universe-legend {
	display: flex;
	gap: 8px;
	margin-right: 6px;
}

.universe-legend-item {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	font-weight: 400;
	color: rgba(var(--theme-color), 0.4);
	line-height: 1;
}

.universe-legend-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	flex-shrink: 0;
}

/* ---------------------------------------------------------------------------
   Segment tooltip (dark theme)
   --------------------------------------------------------------------------- */

.universe-segment-tooltip {
	position: absolute;
	background: #0b1221;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 8px;
	padding: 10px 16px;
	pointer-events: none;
	z-index: 100;
	text-align: center;
	width: 140px;
}

.universe-segment-tooltip-name {
	font-size: 13px;
	font-weight: 600;
	color: rgba(255, 255, 255, 0.8);
}

.universe-segment-tooltip-sub {
	font-size: 11px;
	color: rgba(255, 255, 255, 0.35);
	margin-top: 2px;
}

.universe-segment-tooltip-count {
	font-size: 20px;
	font-weight: 700;
	color: #fff;
	margin-top: 8px;
	line-height: 1.1;
}

.universe-segment-tooltip-count span {
	font-size: 12px;
	font-weight: 400;
	color: rgba(255, 255, 255, 0.4);
}

.universe-segment-tooltip-hint {
	font-size: 11px;
	color: rgba(255, 255, 255, 0.4);
	margin-top: 8px;
}

</style>

<style>

/* When universe is fullscreen (portaled to top), lift the modal panels above it */
.universe-fullscreen-active .panels {
	z-index: 1000000000 !important;
}

</style>
